
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font, Svg, Path, Defs, LinearGradient, Stop, Polygon, Line, Circle, G, Tspan } from '@react-pdf/renderer';

// Register Emoji Source (Twemoji)
Font.registerEmojiSource({
    format: 'png',
    url: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/",
});

// Import Static Rubric Data
import scoreRubric from '../data/cbt_score_justification.json';

// --- HELPER: Icon Components ---
// Simple SVG icons for speedometer boxes (top left corner)
const CompetencyIcon = () => (
    <Svg width="18" height="18" viewBox="0 0 24 24">
        {/* Graduation cap icon for clinical competency */}
        <Path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="#3B82F6" />
    </Svg>
);

const SafetyIcon = () => (
    <Svg width="18" height="18" viewBox="0 0 24 24">
        {/* Shield icon for safety */}
        <Path d="M12 2L4 5V11C4 16.55 7.16 21.74 12 23C16.84 21.74 20 16.55 20 11V5L12 2Z" fill="#059669" />
        {/* Checkmark inside shield - using filled path */}
        <Path d="M9 12L10.5 13.5L15 9L13.5 7.5L10.5 10.5L9 12Z" fill="#FFFFFF" />
    </Svg>
);

const OptimizationIcon = () => (
    <Svg width="18" height="18" viewBox="0 0 24 24">
        {/* Steps/stairs with person climbing up for optimization */}
        {/* Steps */}
        <Path d="M3 20H8V15H3V20Z" fill="#6366F1" />
        <Path d="M8 20H13V12H8V20Z" fill="#6366F1" />
        <Path d="M13 20H18V9H13V20Z" fill="#6366F1" />
        <Path d="M18 20H21V6H18V20Z" fill="#6366F1" />
        {/* Person climbing - simplified stick figure */}
        <Circle cx="15" cy="7" r="2" fill="#6366F1" />
        <Path d="M15 9L15 11L13 13" stroke="#6366F1" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <Path d="M15 11L17 13" stroke="#6366F1" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <Path d="M14 10L16 10" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" />
    </Svg>
);

/**
 * Curved label along a circular arc. @react-pdf/renderer has no SVG textPath support,
 * so we place each glyph on the arc with translate + rotate (tangential orientation).
 */
const curvedGlyphsAlongArc = ({
    cx,
    cy,
    radius,
    centerAngleDeg,
    text,
    fontSize,
    fill,
    fontFamily = 'Helvetica-Bold',
    keyPrefix = 'arc',
}) => {
    const chars = [...text];
    const n = chars.length;
    if (!n) return null;
    const avgCharWidth = fontSize * 0.52;
    const linearSpan = avgCharWidth * Math.max(n - 1, 0.001);
    const angleSpanRad = linearSpan / radius;
    const centerAngleRad = (centerAngleDeg * Math.PI) / 180;

    return chars.map((ch, i) => {
        const t = n === 1 ? 0.5 : i / (n - 1);
        const offsetFromCenter = (t - 0.5) * angleSpanRad;
        const angle = centerAngleRad + offsetFromCenter;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        const rotDeg = (angle * 180) / Math.PI + 90;
        const glyph = ch === ' ' ? '\u00A0' : ch;
        return (
            <G key={`${keyPrefix}-${i}`} transform={`translate(${x}, ${y})`}>
                <G transform={`rotate(${rotDeg})`}>
                    <Text fill={fill} style={{ fontFamily, fontSize }}>
                        <Tspan x={0} y={0} textAnchor="middle" dominantBaseline="central">
                            {glyph}
                        </Tspan>
                    </Text>
                </G>
            </G>
        );
    });
};

// --- HELPER: Speedometer Component ---
// Renders a glowing arc with a needle to indicate score (0-100)
// thresholdScore: score at which to draw dotted line and change color transition
// label: text to display below the score (e.g., "CLINICAL COMPETENCY")
const Speedometer = ({ score, needleScore = null, size = 120, thresholdScore = null, label = 'CLINICAL COMPETENCY' }) => {
    const cx = size / 2;
    const cy = size * 0.8; // Move center down based on size
    const r = size * 0.45; // slightly larger radius
    const startAngle = -180;
    const endAngle = 0;

    // Convert degrees to radians
    const degToRad = (deg) => (deg * Math.PI) / 180;

    // Polar to Cartesian
    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
        const angleInRadians = degToRad(angleInDegrees);
        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians))
        };
    };

    // Create Arc Path
    const createArc = (x, y, radius, startAngle, endAngle) => {
        const start = polarToCartesian(x, y, radius, endAngle);
        const end = polarToCartesian(x, y, radius, startAngle);
        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
        const d = [
            "M", start.x, start.y,
            "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
        ].join(" ");
        return d;
    };

    const getSafetyColor = (val) => {
        if (val <= 70) return '#EF4444';
        if (val <= 80) return '#F97316';
        if (val <= 90) return '#EAB308';
        return '#22C55E';
    };

    // Create multiple arc segments for gradient simulation (fallback if gradient doesn't work)
    const createGradientArcs = () => {
        if (label === 'SAFETY COMPLIANCE') {
            return [{
                path: createArc(cx, cy, r, -180, 0),
                color: getSafetyColor(score)
            }];
        }

        const segments = 30; // Number of segments for smooth gradient
        const arcRange = 180; // Total arc range in degrees
        const segmentAngle = arcRange / segments;

        // Normalize threshold to 0-1 range
        const threshold = thresholdScore !== null ? Math.min(100, Math.max(0, thresholdScore)) / 100 : null;

        // Helper to interpolate color
        const interpolateColor = (color1, color2, t) => {
            const hex = (c) => {
                const x = parseInt(c, 16);
                return isNaN(x) ? 0 : x;
            };
            const r1 = hex(color1.slice(1, 3));
            const g1 = hex(color1.slice(3, 5));
            const b1 = hex(color1.slice(5, 7));
            const r2 = hex(color2.slice(1, 3));
            const g2 = hex(color2.slice(3, 5));
            const b2 = hex(color2.slice(5, 7));
            const r = Math.round(r1 + (r2 - r1) * t).toString(16).padStart(2, '0');
            const g = Math.round(g1 + (g2 - g1) * t).toString(16).padStart(2, '0');
            const b = Math.round(b1 + (b2 - b1) * t).toString(16).padStart(2, '0');
            return `#${r}${g}${b}`;
        };

        return Array.from({ length: segments }, (_, i) => {
            const start = -180 + (i * segmentAngle);
            const end = -180 + ((i + 1) * segmentAngle);
            const progress = i / (segments - 1);

            let color;

            if (threshold !== null && threshold > 0 && threshold < 1) {
                // Before threshold: all red
                if (progress < threshold) {
                    color = '#EF4444'; // Red
                } else {
                    // After threshold: transition from red to green
                    const postThresholdProgress = (progress - threshold) / (1 - threshold);
                    const colorStops = [
                        { pos: 0, color: '#EF4444' },    // Red
                        { pos: 0.25, color: '#F97316' },  // Orange
                        { pos: 0.5, color: '#EAB308' },   // Yellow
                        { pos: 0.75, color: '#84CC16' }, // Yellow-Green
                        { pos: 1, color: '#22C55E' }      // Green
                    ];

                    // Find the two color stops to interpolate between
                    for (let j = 0; j < colorStops.length - 1; j++) {
                        if (postThresholdProgress >= colorStops[j].pos && postThresholdProgress <= colorStops[j + 1].pos) {
                            const color1 = colorStops[j].color;
                            const color2 = colorStops[j + 1].color;
                            const t = (postThresholdProgress - colorStops[j].pos) / (colorStops[j + 1].pos - colorStops[j].pos);
                            color = interpolateColor(color1, color2, t);
                            break;
                        }
                    }
                    if (!color) {
                        color = '#22C55E'; // Default to green if no match
                    }
                }
            } else {
                // No threshold: use original gradient
                const colorStops = [
                    { pos: 0, color: '#EF4444' },    // Red
                    { pos: 0.25, color: '#F97316' },  // Orange
                    { pos: 0.5, color: '#EAB308' },   // Yellow
                    { pos: 0.75, color: '#84CC16' }, // Yellow-Green
                    { pos: 1, color: '#22C55E' }      // Green
                ];

                for (let j = 0; j < colorStops.length - 1; j++) {
                    if (progress >= colorStops[j].pos && progress <= colorStops[j + 1].pos) {
                        const color1 = colorStops[j].color;
                        const color2 = colorStops[j + 1].color;
                        const t = (progress - colorStops[j].pos) / (colorStops[j + 1].pos - colorStops[j].pos);
                        color = interpolateColor(color1, color2, t);
                        break;
                    }
                }
            }

            return {
                path: createArc(cx, cy, r, start, end),
                color: color || '#EF4444'
            };
        });
    };

    // Calculate threshold angle for dotted line
    const thresholdScoreNum = thresholdScore !== null ? parseFloat(thresholdScore) : null;
    const thresholdAngleDeg = (thresholdScoreNum !== null && !isNaN(thresholdScoreNum))
        ? -180 + (Math.min(100, Math.max(0, thresholdScoreNum)) / 100) * 180
        : null;
    const thresholdAngleRad = thresholdAngleDeg !== null ? degToRad(thresholdAngleDeg) : null;

    // Needle Angle
    const activeNeedleScore = needleScore !== null ? needleScore : score;
    const clampedNeedleScore = Math.min(100, Math.max(0, activeNeedleScore));
    const needleAngleDeg = -180 + (clampedNeedleScore / 100) * 180;
    const needleAngleRad = degToRad(needleAngleDeg);

    // Sharp Needle Geometry (Triangle)
    const needleLen = r + 5;
    const needleBaseDist = r - 25; // Floating start
    const needleBaseWidth = 6; // Width of the triangle base

    // Tip of the needle (sharp point)
    const tipX = cx + (needleLen * Math.cos(needleAngleRad));
    const tipY = cy + (needleLen * Math.sin(needleAngleRad));

    // Base Center
    const baseX = cx + (needleBaseDist * Math.cos(needleAngleRad));
    const baseY = cy + (needleBaseDist * Math.sin(needleAngleRad));

    // Calculate Perpendicular Vector for Base
    // Perpendicular angle = angle + 90 deg (PI/2)
    const perpAngle = needleAngleRad + (Math.PI / 2);

    // Base Corner 1
    const b1X = baseX + ((needleBaseWidth / 2) * Math.cos(perpAngle));
    const b1Y = baseY + ((needleBaseWidth / 2) * Math.sin(perpAngle));

    // Base Corner 2
    const b2X = baseX - ((needleBaseWidth / 2) * Math.cos(perpAngle));
    const b2Y = baseY - ((needleBaseWidth / 2) * Math.sin(perpAngle));

    return (
        <View style={styles.speedometerContainer}>
            <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ marginTop: -15 }}>
                <Defs>
                    {/* Inner Shadow Gradient for Center Hub */}
                    <LinearGradient id="gradHub" x1="0%" y1="0%" x2="0%" y2="100%">
                        <Stop offset="0%" stopColor="#1E293B" /> {/* Dark Slate */}
                        <Stop offset="100%" stopColor="#0F172A" /> {/* Darker Slate */}
                    </LinearGradient>

                    {/* Smooth Red -> Green Gradient - Horizontal gradient for arc */}
                    <LinearGradient
                        id={`speedometerGradient-${size}`}
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                    >
                        <Stop offset="0%" stopColor="#EF4444" />   {/* Red */}
                        <Stop offset="25%" stopColor="#F97316" />  {/* Orange */}
                        <Stop offset="50%" stopColor="#EAB308" />  {/* Yellow */}
                        <Stop offset="75%" stopColor="#84CC16" />  {/* Yellow-Green */}
                        <Stop offset="100%" stopColor="#22C55E" /> {/* Green */}
                    </LinearGradient>
                </Defs>

                {/* --- 1. SMOOTH GRADIENT ARC (Red -> Green) --- */}
                {/* Using segmented arcs as React-PDF may not support gradients in strokes properly */}
                {createGradientArcs().map((segment, idx) => (
                    <Path
                        key={idx}
                        d={segment.path}
                        stroke={segment.color}
                        strokeWidth={16}
                        fill="none"
                        strokeLinecap="round"
                    />
                ))}

                {/* --- RECESSED HUB (Dark Semi-Circle) --- */}
                {/* We simulate specific look by adding a large dark arc "inside" the color track */}
                <Path
                    d={createArc(cx, cy, r - 12, -180, 0)}
                    stroke="#1E293B"
                    strokeWidth={4}
                    fill="none"
                    strokeOpacity={0.1}
                />

                {/* --- THRESHOLD DOTTED LINE (at adjusted_expectation score) --- */}
                {thresholdAngleRad !== null && thresholdScore !== null && thresholdScore > 0 && thresholdScore < 100 && (() => {
                    const lineStartX = cx + ((r - 25) * Math.cos(thresholdAngleRad));
                    const lineStartY = cy + ((r - 25) * Math.sin(thresholdAngleRad));
                    const lineEndX = cx + ((r + 28) * Math.cos(thresholdAngleRad));
                    const lineEndY = cy + ((r + 28) * Math.sin(thresholdAngleRad));

                    // Create dashed line using Path (more reliable in React-PDF)
                    const dashLength = 4;
                    const gapLength = 3;
                    const totalLength = Math.sqrt(Math.pow(lineEndX - lineStartX, 2) + Math.pow(lineEndY - lineStartY, 2));
                    const numDashes = Math.floor(totalLength / (dashLength + gapLength));
                    const dx = (lineEndX - lineStartX) / totalLength;
                    const dy = (lineEndY - lineStartY) / totalLength;

                    let pathData = '';
                    for (let i = 0; i < numDashes; i++) {
                        const dashStart = i * (dashLength + gapLength);
                        const dashEnd = dashStart + dashLength;
                        const x1 = lineStartX + (dashStart * dx);
                        const y1 = lineStartY + (dashStart * dy);
                        const x2 = lineStartX + (Math.min(dashEnd, totalLength) * dx);
                        const y2 = lineStartY + (Math.min(dashEnd, totalLength) * dy);
                        if (i === 0) {
                            pathData += `M ${x1} ${y1} L ${x2} ${y2}`;
                        } else {
                            pathData += ` M ${x1} ${y1} L ${x2} ${y2}`;
                        }
                    }

                    return (
                        <Path
                            d={pathData}
                            stroke="#6366F1"
                            strokeWidth={1.5}
                            strokeOpacity={0.8}
                            strokeLinecap="round"
                        />
                    );
                })()}

                {/* --- SHARP NEEDLE (Black Triangle) --- */}
                <Polygon
                    points={`${tipX},${tipY} ${b1X},${b1Y} ${b2X},${b2Y}`}
                    fill="#111827" // Almost Black (Gray-900)
                />

                {/* Org expectation label follows the arc (no textPath in react-pdf) */}
                {thresholdAngleRad !== null &&
                    thresholdScore !== null &&
                    thresholdScore > 0 &&
                    thresholdScore < 100 &&
                    thresholdAngleDeg !== null && (
                        <G>
                            {curvedGlyphsAlongArc({
                                cx,
                                cy,
                                radius: r + 30,
                                centerAngleDeg: thresholdAngleDeg,
                                text: 'Org Expectation',
                                fontSize: 8,
                                fill: '#6366F1',
                                keyPrefix: 'org-exp',
                            })}
                            {curvedGlyphsAlongArc({
                                cx,
                                cy,
                                radius: r + 20,
                                centerAngleDeg: thresholdAngleDeg,
                                text: `     ${Math.round(thresholdScore)} %`,
                                fontSize: 8,
                                fill: '#6366F1',
                                keyPrefix: 'org-pct',
                            })}
                        </G>
                    )}

                {/* Pivot Circle Removed as requested */}{/* Pivot Circle Removed as requested */}
            </Svg>

            {/* Score Text Overlay - Centered vertically in the box */}
            <View style={styles.speedometerScoreOverlay}>
                <Text style={[styles.speedometerScoreValue, label === 'SAFETY COMPLIANCE' && { color: getSafetyColor(score) }]}>
                    {Math.round(score)}%
                </Text>
                <Text style={styles.speedometerScoreLabel}>{label}</Text>
            </View>
        </View>
    );
};


// --- Theme & Styles ---
const theme = {
    primary: '#1F2937',    // Dark Gray/Slate
    secondary: '#D97D3A',  // Brand Amber
    accent: '#4682B4',     // Steel Blue
    text: '#374151',       // Neutral 700
    textLight: '#6B7280',  // Neutral 500
    bgLight: '#F3F4F6',    // Neutral 100
    bgWhite: '#FFFFFF',
    border: '#E5E7EB',     // Neutral 200
    success: '#059669',    // Green 600
    danger: '#DC2626',     // Red 600
    playbookBg: '#FFF9F0', // Cream/Amber light
    playbookBorder: '#F59E0B' // Amber
};

// Get the current year using JavaScript
const currentYear = new Date().getFullYear()

const styles = StyleSheet.create({
    page: {
        // No top/left/right padding — header bleeds to edges natively.
        // Content below the header is padded via the pageContent wrapper.
        paddingTop: 0,
        paddingBottom: 50, // Increased bottom padding for fixed footer
        paddingLeft: 0,
        paddingRight: 0,
        fontFamily: 'Helvetica',
        fontSize: 10,
        color: theme.text,
        backgroundColor: '#FFFFFF',
    },
    // Wrapper for all content BELOW the header (restores original side/top padding)
    pageContent: {
        paddingTop: 10,
        paddingLeft: 35,
        paddingRight: 35,
    },
    // --- Header ---
    headerContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        // No negative margins — padding achieves the edge-bleed look instead
        paddingTop: 16,
        paddingLeft: 35,
        paddingRight: 35,
        paddingBottom: 8,
        backgroundColor: '#F3F4F6', // Header Background
        marginBottom: 2,
        borderBottomWidth: 1,
        borderBottomColor: '#D1D5DB',
    },
    headerLogoGrouping: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    logo: {
        width: 40,
        height: 40,
        marginRight: 10,
    },
    headerTitleMain: {
        fontSize: 18,
        fontFamily: 'Helvetica-Bold',
        color: theme.primary,
        textTransform: 'uppercase',
    },
    headerSubTitle: {
        fontSize: 10,
        color: theme.secondary,
        fontWeight: 'bold',
        letterSpacing: 1,
    },
    headerDateMeta: {
        textAlign: 'right',
    },

    // --- Section Commons ---
    section: {
        marginBottom: 15,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: theme.border,
        paddingBottom: 5,
    },
    sectionTitle: {
        fontSize: 14,
        fontFamily: 'Helvetica-Bold',
        color: theme.primary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },

    // --- Metadata Grid ---
    metaGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        backgroundColor: theme.bgLight,
        borderRadius: 6,
        padding: 5,
        marginBottom: 10,
        marginTop: 0,
    },
    metaItem: {
        width: '20%', // 5 items
        marginBottom: 5,
    },
    metaLabel: {
        fontSize: 7,
        color: theme.textLight,
        textTransform: 'uppercase',
        marginBottom: 2,
        fontWeight: 'bold',
    },
    metaValue: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: theme.primary,
    },

    // --- Cards (Generic) ---
    card: {
        backgroundColor: theme.bgWhite,
        borderRadius: 6,
        padding: 10,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: theme.border,
    },
    cardTitle: {
        fontSize: 9,
        fontWeight: 'bold',
        color: theme.secondary,
        marginBottom: 5,
        textTransform: 'uppercase',
    },
    textBody: {
        fontSize: 10,
        lineHeight: 1.4,
        color: theme.text,
    },

    // --- Scores ---
    scoreContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
        gap: 15,
    },
    scoreBox: {
        width: '48%',
        padding: 15,
        borderRadius: 8,
        borderWidth: 1,
        alignItems: 'center',
    },
    scoreLabel: {
        fontSize: 10,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginBottom: 5,
    },
    scoreBigNumber: {
        fontSize: 24,
        fontFamily: 'Helvetica-Bold',
    },

    // --- Styled Boxes (Observations & Strengths, Optimization Opportunity) ---
    styledBoxContainer: {
        position: 'relative',
        width: '100%',
        marginBottom: 12,
    },
    styledBoxShadow: {
        position: 'absolute',
        top: 3,
        left: 3,
        right: -3,
        bottom: -3,
        borderRadius: 10,
    },
    styledBoxShadowGreen: {
        backgroundColor: '#D1FAE5', // Light green/teal shadow
    },
    styledBoxShadowPink: {
        backgroundColor: '#FEE2E2', // Light pink/red shadow
    },
    styledBox: {
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
        borderWidth: 2,
        position: 'relative',
    },
    styledBoxGreen: {
        borderColor: '#86EFAC', // Green/teal border
        boxShadow: '0 4px 6px rgba(16, 185, 129, 0.15)',
    },
    styledBoxPink: {
        borderColor: '#FCA5A5', // Pink/red border
        boxShadow: '0 4px 6px rgba(239, 68, 68, 0.15)',
    },
    styledBoxPadding: {
        paddingTop: 5,
        paddingLeft: 10,
        paddingRight: 10,
    },
    styledBoxPaddingBottom: {
        paddingBottom: 0,
    },
    styledBoxPaddingBottomSmall: {
        paddingBottom: 0,
    },
    styledBoxHeader: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    styledBoxHeaderWithContent: {
        marginBottom: 12,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    styledBoxHeaderNoContent: {
        marginBottom: 0,
        paddingBottom: 0,
        borderBottomWidth: 0,
    },
    styledBoxAccentBar: {
        width: 4,
        height: 16,
        borderRadius: 2,
        marginRight: 8,
    },
    styledBoxAccentBarGreen: {
        backgroundColor: '#10B981',
    },
    styledBoxAccentBarRed: {
        backgroundColor: '#EF4444',
    },
    styledBoxTitle: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        color: '#1E293B',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
    },
    styledBoxContent: {
        gap: 8,
        marginTop: 4,
    },
    styledBoxItem: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 6,
        paddingLeft: 4,
    },
    styledBoxBullet: {
        width: 6,
        height: 6,
        borderRadius: 3,
        marginTop: 4,
        marginRight: 8,
        flexShrink: 0,
    },
    styledBoxBulletGreen: {
        backgroundColor: '#10B981',
    },
    styledBoxBulletRed: {
        backgroundColor: '#EF4444',
    },
    styledBoxText: {
        fontSize: 9.5,
        color: '#475569',
        lineHeight: 1.5,
        flex: 1,
    },

    // --- Speedometer Styles ---
    speedometerContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        overflow: 'visible',
    },
    speedometerScoreOverlay: {
        position: 'absolute',
        top: 45,
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    speedometerScoreValue: {
        fontSize: 22,
        fontFamily: 'Helvetica-Bold',
        color: '#3B82F6',
    },
    speedometerScoreLabel: {
        fontSize: 8,
        color: '#64748B',
        marginTop: 4,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    speedometerBox: {
        backgroundColor: '#FFFFFF',
        borderColor: '#E2E8F0',
        alignItems: 'center',
        justifyContent: 'center',
        height: 110,
        overflow: 'visible',
        position: 'relative',
    },
    speedometerIconContainer: {
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 10,
    },

    // --- Competency Item ---
    competencyItemContainer: {
        borderWidth: 0.5,
        borderColor: '#D1D5DB',
        borderRadius: 8,
        padding: 12,
        marginBottom: 20,
    },
    competencyHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    competencyTitle: {
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        color: '#1F2937',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },
    competencyScoreBadge: {
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 12,
    },
    competencyScoreText: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
    },
    competencySection: {
        marginBottom: 14,
    },
    competencySectionTitle: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    },
    competencySectionContent: {
        fontSize: 9,
        color: '#374151',
        lineHeight: 1.5,
    },
    competencyTwoColumn: {
        flexDirection: 'row',
        gap: 12,
        marginBottom: 14,
    },
    competencyBox: {
        flex: 1,
        backgroundColor: '#FAFBFC',
        borderRadius: 6,
        padding: 10,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    competencyFullWidth: {
        backgroundColor: '#FAFBFC',
        borderRadius: 6,
        padding: 12,
        marginBottom: 14,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderLeftWidth: 3,
    },
    competencyExample: {
        fontSize: 9,
        fontStyle: 'italic',
        color: '#4B5563',
        marginTop: 6,
        paddingLeft: 8,
        borderLeftWidth: 2,
        borderLeftColor: '#CBD5E1',
    },
    competencyBullet: {
        fontSize: 9,
        color: '#374151',
        lineHeight: 1.5,
        marginBottom: 4,
    },
    competencyContextBox: {
        backgroundColor: '#F9FAFB',
        borderRadius: 6,
        padding: 10,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    skillContainer: {
        marginBottom: 15,
        borderWidth: 1,
        borderColor: theme.border,
        borderRadius: 6,
        padding: 0,
        overflow: 'hidden',
    },
    skillHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: theme.bgLight,
        padding: 8,
        borderBottomWidth: 1,
        borderBottomColor: theme.border,
    },
    skillName: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: theme.primary,
    },
    skillScore: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: theme.secondary,
    },
    skillContent: {
        padding: 10,
    },
    skillLabel: {
        fontSize: 8,
        color: theme.textLight,
        fontWeight: 'bold',
        marginBottom: 2,
        marginTop: 6,
        textTransform: 'uppercase',
    },

    // --- Expert Box ---
    expertBox: {
        marginTop: 10,
        backgroundColor: theme.playbookBg,
        borderRadius: 4,
        borderLeftWidth: 3,
        borderLeftColor: theme.playbookBorder,
        padding: 8,
    },
    expertRow: {
        flexDirection: 'row',
        marginBottom: 8, // More spacing between Pitfall and Rationale
    },
    expertIconCol: {
        width: 25,
    },
    expertTextCol: {
        flex: 1,
        flexDirection: 'column', // Enforce stacking
    },
    expertLabel: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 9,
        color: '#92400E', // Dark Amber
        marginBottom: 3, // Space between header and text
        display: 'block',
    },
    expertText: {
        fontSize: 9,
        color: theme.text,
        lineHeight: 1.3,
    },

    // --- Transcript ---
    transcriptItem: {
        marginBottom: 12,
        paddingBottom: 2, // Reduced padding
    },
    speakerLabel: {
        fontSize: 8,
        fontWeight: 'bold',
        marginBottom: 2,
        width: 60,
    },
    dialogueRow: {
        flexDirection: 'row',
    },
    bubble: {
        flex: 1,
        padding: 8,
        borderRadius: 6,
    },
    docBubble: {
        backgroundColor: '#F3F4F6',
    },
    patBubble: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },

    // --- Footers ---
    explanationSection: {
        marginTop: 30,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: theme.border,
        marginBottom: 10,
    },
    pageFooter: {
        position: 'absolute',
        bottom: 15,
        left: 35,
        right: 35,
        borderTopWidth: 1,
        borderTopColor: theme.border,
        paddingTop: 10,
    },
    footerText: {
        fontSize: 8,
        color: theme.textLight,
        marginBottom: 2,
    },
    // --- Missing Styles Restored ---
    metaCol: {
        marginBottom: 5,
    },
    label: {
        fontSize: 8,
        color: theme.textLight,
        fontWeight: 'bold',
        marginBottom: 2,
        textTransform: 'uppercase',
    },
    value: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: theme.primary,
    },
    hr: {
        borderBottomWidth: 1,
        borderBottomColor: theme.border,
        marginVertical: 10,
        width: '100%',
    },
    badge: {
        paddingVertical: 2,
        paddingHorizontal: 6,
        marginLeft: 4,
        alignItems: 'center',
        justifyContent: 'center',
    },
});




// Helper to get overall likelihood/competency score
const getOverallScore = (evalData) => {
    if (!evalData) return 0;

    // Try to get from scoring_breakdown first (Report Format V2)
    const sb = evalData.scoring_breakdown;
    if (sb && (sb.uncapped_competency_pct !== undefined || sb.competency_score !== undefined)) {
        return Math.round(sb.uncapped_competency_pct || sb.competency_score);
    }

    if (evalData?.counsellor_interventions_and_skills) {
        let total = 0;
        let count = 0;
        Object.values(evalData.counsellor_interventions_and_skills).forEach(skill => {
            if (skill && typeof skill.score === 'number') {
                total += skill.score;
                count++;
            }
        });
        if (count > 0) {
            const activeScale = evalData?.rubric_info?.scale_max || 6;
            return Math.round((total / (count * activeScale)) * 100);
        }
    }

    return 0;
};

// --- SVG MATH HELPERS ---

// 1. Radar Chart Helpers
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    var angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
    };
};

const makePolygonPoints = (centerX, centerY, radius, sides, data) => {
    let points = "";
    const angleStep = 360 / sides;
    for (let i = 0; i < sides; i++) {
        const value = data[i] || 0; // Normalized 0-1
        const r = radius * value;
        const pos = polarToCartesian(centerX, centerY, r, i * angleStep);
        points += `${pos.x},${pos.y} `;
    }
    return points;
};

const makeWebGrid = (centerX, centerY, radius, sides, steps = 5) => {
    let webs = [];
    for (let i = 1; i <= steps; i++) {
        const r = (radius / steps) * i;
        webs.push(makePolygonPoints(centerX, centerY, r, sides, Array(sides).fill(1))); // Full ring
    }
    return webs;
};

// 2. Gauge/Arc Helper
const describeArc = (x, y, radius, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    const d = [
        "M", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
    return d;
};

const ConsultationPDFDocument = ({
    consultationData,
    patientDetails,
    doctorDetails,
    conversationEntries = [],
    cognitiveReconstruction = null,
    benchmarkData = [],
    benchmarkThresholds = {
        confidence_threshold: 0.35,
        confidence_playbook_threshold: 0.3,
        distance_threshold: 0.85,
        distance_playbook_threshold: 0.9
    },
    consultationId,
    downloadMode = 'full', // 'full', 'summary_only', 'report_no_transcript'
    logoDataUrl = null,
}) => {

    // --- DATA PREPARATION (Standard-Aware) ---
    const supervisorReport = (() => {
        const raw = consultationData?.supervisor_evaluation || null;
        if (!raw) return null;
        let report = null;
        if (typeof raw === 'string') {
            try { report = JSON.parse(raw); } catch (e) { return null; }
        } else {
            report = raw;
        }

        if (report && report.counsellor_interventions_and_skills) {
            const rubricInf = report.rubric_info || {};
            const scaleMax = rubricInf.scale_max || 6.0;
            const hideThresh = scaleMax * 0.6;

            // Hide optional competencies whose score is below 60% of the scale max
            const processedSkills = Object.fromEntries(
                Object.entries(report.counsellor_interventions_and_skills).map(([k, skill]) => {
                    const shouldHide = skill.optional && (skill.score === undefined || skill.score === null || skill.score < hideThresh);
                    return [k, { ...skill, hideFromPDF: shouldHide }];
                })
            );
            return {
                ...report,
                counsellor_interventions_and_skills: processedSkills
            };
        }
        return report;
    })();

    // --- Logic Helpers ---
    const showDetailedContent = downloadMode !== 'summary_only';
    const showTranscript = downloadMode === 'full';
    const isGuideOnly = downloadMode === 'guide_only';

    const getTherapistReflection = () => {
        if (!consultationData?.selfreflection) return null;
        try {
            const parsed = typeof consultationData.selfreflection === 'string'
                ? JSON.parse(consultationData.selfreflection)
                : consultationData.selfreflection;
            if (parsed && typeof parsed === 'object') return parsed;
        } catch (e) {
            // Fallback for plain text legacy reflections
            return { effective: consultationData.selfreflection, stuck: "", next_time: "" };
        }
        return null;
    };
    const therapistReflection = getTherapistReflection();

    const rubricInfo = supervisorReport?.rubric_info || supervisorReport?.scoring_breakdown?.rubric_info || {
        scale_max: 6,
        standard_name: "CTS-R"
    };
    const scaleMax = rubricInfo.scale_max || 6;
    const satisfactoryThreshold = rubricInfo.satisfactory_threshold || scaleMax * 0.5;

    const skills = supervisorReport?.counsellor_interventions_and_skills || {};
    const skillKeys = Object.keys(skills).filter(k => !skills[k].hideFromPDF);

    const truncateText = (text, maxLength = 220) => {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    // Helper to get score color (MOVE INSIDE TO ACCESS scaleMax)
    const getScoreColor = (score) => {
        if (score >= (scaleMax * 0.67)) return theme.success;
        if (score >= (scaleMax * 0.5)) return theme.secondary;
        return theme.danger;
    };

    const getPercentageColor = (score) => {
        if (!score) return theme.text;
        if (score >= 80) return theme.success;
        if (score <= 50) return theme.danger;
        return '#D97706'; // Amber-600 for warning/average
    };

    const getBioNarrative = () => {
        // Primary: previous_session_hypothesis if available
        const prevHypothesis = supervisorReport?.previous_session_hypothesis?.trim();
        if (prevHypothesis && prevHypothesis.length > 10) {
            return prevHypothesis;
        }

        // Secondary: virtual patient profile (trauma memory narrative)
        let vp = null;
        if (patientDetails?.virtual_profile) {
            try {
                vp = typeof patientDetails.virtual_profile === 'string'
                    ? JSON.parse(patientDetails.virtual_profile)
                    : patientDetails.virtual_profile;
            } catch (e) { }
        }
        const pName = patientDetails?.name || "Patient";
        const pAgeGroup = patientDetails?.age_group || (vp ? vp.age_group : "adult");
        const pGender = patientDetails?.gender || "individual";
        const pRel = patientDetails?.relationshipstatus || (vp ? vp.relationshipstatus : "single");
        const eventSummary = vp?.trauma_memory?.event_summary || "";
        const shamePoint = vp?.trauma_memory?.shame_point || "";
        if (eventSummary) {
            return `${pName}, a ${pRel} ${pAgeGroup} ${pGender}. ${eventSummary}. ${shamePoint ? "Core shame: " + shamePoint : ""}`;
        }

        // Tertiary: static patient biography/history fields
        const staticBio = patientDetails?.biography || patientDetails?.history || "";
        if (staticBio.length > 30) {
            return staticBio;
        }

        // Fallback: history_summary from supervisor report
        return supervisorReport?.history_summary?.replace(/\*\*/g, "").split(/EXPERT CLINICAL SUPERVISION ANALYSIS:?/i)[0].trim() || "N/A";
    };

    const getTopBenchmarks = () => {
        // Use organization-configured thresholds (with defaults)
        const confThreshold = benchmarkThresholds.confidence_threshold || 0.35;
        const confPlaybookThreshold = benchmarkThresholds.confidence_playbook_threshold || 0.3;
        const distThreshold = benchmarkThresholds.distance_threshold || 0.85;
        const distPlaybookThreshold = benchmarkThresholds.distance_playbook_threshold || 0.9;

        return benchmarkData
            .map((b, i) => b ? { ...b, originalIndex: i + 1 } : null)
            .filter(b => {
                if (!b) return false;

                // Debug Log
                const logPrefix = `[PDF Debug] Turn ${b.originalIndex}:`;

                // Basic content check - Use organization-configured thresholds
                const isPlaybook = Boolean(b.why_correct && b.why_correct.length > 3);
                const conf = b.confidence || 0;
                const dist = b.distance || 1.0;

                let hasContent = false;
                if (conf > 0) {
                    if (conf >= confThreshold) hasContent = true;
                    else if (isPlaybook && conf >= confPlaybookThreshold) hasContent = true;
                } else {
                    if (dist < distThreshold) hasContent = true;
                    else if (isPlaybook && dist < distPlaybookThreshold) hasContent = true;
                }

                // Also check that we have expert_response
                hasContent = hasContent && Boolean(b.expert_response);

                if (!hasContent) {
                    //console.log(`${logPrefix} Filtered - No valid content (why_correct=${!!b.why_correct}, dist=${dist}, conf=${conf})`);
                    return false;
                }

                // Context Length Check (User Request: Ignore filler words / < 4 words)
                const context = getContextForTurn(b.originalIndex);
                if (!context) {
                    //console.log(`${logPrefix} Filtered - No Context`);
                    return false;
                }

                const wordCount = context.trim().split(/\s+/).length;
                if (wordCount < 4) {
                    //console.log(`${logPrefix} Filtered - Context too short (${wordCount} words)`);
                    return false;
                }

                //console.log(`${logPrefix} Included!`);
                return true;
            })
            .sort((a, b) => (b.confidence || 0) - (a.confidence || 0))
            .slice(0, 5);
    };

    // Computed once per render — getTopBenchmarks() re-scans the entire
    // benchmarkData array (map+filter+sort), so calling it inside the
    // per-turn transcript loop was O(turns x benchmarks) and froze the page
    // for long sessions in 'full' download mode. Reuse this single result.
    const topBenchmarksList = getTopBenchmarks();
    const topBenchmarkTurnIndices = new Set(topBenchmarksList.map(bm => bm.originalIndex));

    const getContextForTurn = (originalIndex) => {
        const idx = originalIndex - 2;
        if (idx >= 0 && conversationEntries[idx]) return conversationEntries[idx].patient;
        return null;
    };

    // Normalize common therapy acronyms to uppercase (e.g., cbt -> CBT)
    // Only when they appear as standalone words followed by a space.
    const normalizeTherapyAcronyms = (text) => {
        if (!text) return text;
        return text.replace(/\b(cbt|rebt|sfbt)\b(?=\s)/gi, (match) => match.toUpperCase());
    };

    // Get presenting condition with priority order
    const getPresentingCondition = () => {
        const supervisorEval = consultationData?.supervisor_evaluation;
        return supervisorEval?.presenting_condition ||
            supervisorEval?.clinical_diagnosis?.reasoning ||
            patientDetails?.conditions ||
            patientDetails?.condition ||
            "Unspecified";
    };

    // Get evaluation framework name based on therapy type
    const getEvaluationFramework = () => {
        const name = rubricInfo?.standard_name || "";
        const id = rubricInfo?.standard_id || "";

        if (name.toUpperCase().includes("NIMHANS") || id.toLowerCase().includes("nimhans")) {
            return "NIMHANS Evaluation Framework";
        }

        if (name) return name;

        const therapyType = consultationData?.therapy_type || "CBT";
        const frameworkMap = {
            "CBT": "Revised Cognitive Therapy Scale (CTS-R)",
            "REBT": "Rational Emotive Behavior Therapy Competency Scale",
            "SFBT": "Solution-Focused Brief Therapy Adherence Measure",
            "DBT": "Dialectical Behavior Therapy Adherence Scale"
        };
        return frameworkMap[therapyType] || frameworkMap["CBT"];
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>

                {/* --- HEADER (bleeds edge-to-edge via its own padding, no negative margins) --- */}
                <View style={styles.headerContainer}>
                    <View style={styles.headerLogoGrouping}>
                        <Image
                            src={logoDataUrl || "/media/img/brain_black.png"}
                            style={styles.logo}
                        />
                        <View>
                            <Text style={styles.headerSubTitle}>MindConnect</Text>
                            <Text style={styles.headerTitleMain}>
                                {isGuideOnly ? "Evaluation Framework Guide" : "Clinical Consultation Review"}
                            </Text>
                        </View>
                    </View>
                    <View style={styles.headerDateMeta}>
                        <Text style={{ fontSize: 10, color: theme.textLight }}>Generated On</Text>
                        <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: theme.primary }}>
                            {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Text>
                    </View>
                </View>
                {/* --- TOP SPACER for pages 2+ (fixed, only renders when pageNumber > 1) --- */}
                <View
                    fixed
                    render={({ pageNumber }) =>
                        pageNumber > 1 ? <View style={{ height: 20 }} /> : null
                    }
                />

                {/* --- PAGE CONTENT WRAPPER (restores left/right/top padding below header) --- */}
                <View style={styles.pageContent}>

                    {!isGuideOnly && (
                        <>
                            {/* --- METADATA --- */}
                            <View style={{
                                marginBottom: 5,
                                paddingBottom: 5,
                                borderBottomWidth: 1,
                                borderBottomColor: theme.border,
                            }}>
                                {/* Row 1: Primary Details */}
                                <View style={{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    marginBottom: 6,
                                    columnGap: 20,
                                    rowGap: 4
                                }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                        <Text style={[styles.label, { marginRight: 6, marginBottom: 0 }]}>PATIENT:</Text>
                                        <Text style={styles.value}>{patientDetails.name}</Text>
                                    </View>
                                    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                        <Text style={[styles.label, { marginRight: 6, marginBottom: 0 }]}>DOCTOR:</Text>
                                        <Text style={styles.value}>{doctorDetails.name}</Text>
                                    </View>
                                    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                        <Text style={[styles.label, { marginRight: 6, marginBottom: 0 }]}>MODALITY:</Text>
                                        <Text style={styles.value}>{consultationData.therapy_type || "CBT"}</Text>
                                    </View>

                                    {/* Small Complexity (Row 1) */}
                                    {(() => {
                                        const complexity = supervisorReport?.clinical_diagnosis?.severity || consultationData?.supervisor_evaluation?.clinical_diagnosis?.severity || "";
                                        if (complexity.length <= 12) {
                                            return (
                                                <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                                    <Text style={[styles.label, { marginRight: 6, marginBottom: 0 }]}>COMPLEXITY:</Text>
                                                    <Text style={styles.value}>{complexity}</Text>
                                                </View>
                                            );
                                        }
                                        return null;
                                    })()}
                                </View>

                                {/* Row 2: Large Complexity (Occupies 100% width) */}
                                {(() => {
                                    const complexity = supervisorReport?.clinical_diagnosis?.severity || consultationData?.supervisor_evaluation?.clinical_diagnosis?.severity || "";
                                    if (complexity.length > 12) {
                                        return (
                                            <View style={{ flexDirection: 'row', alignItems: 'baseline', width: '100%' }}>
                                                <Text style={[styles.label, { marginRight: 8, flexShrink: 0, marginBottom: 0 }]}>COMPLEXITY:</Text>
                                                <Text style={[styles.value, { flex: 1, lineHeight: 1.3, fontSize: 10 }]}>
                                                    {complexity}
                                                </Text>
                                            </View>
                                        );
                                    }
                                    return null;
                                })()}
                            </View>

                            {/* --- HERO SECTION: Presenting Condition + Speedometers + SI Banner --- */}
                            {/* Rendered OUTSIDE the main card so it always lands on page 1 */}
                            {supervisorReport && (
                                <View style={{ marginBottom: 8 }}>
                                    {/* Presenting Condition */}
                                    {(() => {
                                        const hypothesis = supervisorReport?.updated_hypothesis;
                                        const hasHypothesis = hypothesis && hypothesis !== "N/A" && hypothesis.trim() !== "";
                                        const displayContent = hasHypothesis ? hypothesis : getPresentingCondition();
                                        if (!displayContent || displayContent === "N/A") return null;
                                        return (
                                            <View wrap={false} style={{
                                                marginBottom: 8,
                                                paddingTop: 3,
                                                paddingBottom: 3,
                                                paddingLeft: 12,
                                                paddingRight: 12,
                                                backgroundColor: '#F0F9FF',
                                                borderWidth: 1,
                                                borderColor: '#BFDBFE',
                                                borderLeftWidth: 4,
                                                borderLeftColor: '#3B82F6',
                                                borderRadius: 6
                                            }}>
                                                <Text style={[styles.cardTitle, {
                                                    fontSize: 11,
                                                    color: '#1E40AF',
                                                    marginBottom: 3,
                                                    fontFamily: 'Helvetica-Bold'
                                                }]}>Patient Presenting Condition</Text>
                                                <Text style={[styles.textBody, {
                                                    fontSize: 10,
                                                    color: '#1E3A8A',
                                                    lineHeight: 1.5
                                                }]}>
                                                    {displayContent}
                                                </Text>
                                            </View>
                                        );
                                    })()}

                                    {/* Speedometers */}
                                    {supervisorReport?.scoring_breakdown && (
                                        <View style={styles.scoreContainer}>
                                            {(() => {
                                                const getThresholdScore = () => {
                                                    const adjustedExpectation = supervisorReport.scoring_breakdown?.adjusted_expectation;
                                                    return adjustedExpectation?.score
                                                        || adjustedExpectation
                                                        || (typeof adjustedExpectation === 'number' ? adjustedExpectation : null);
                                                };
                                                return (
                                                    <>
                                                        <View style={[styles.scoreBox, styles.speedometerBox, { marginRight: 10 }]}>
                                                            <View style={styles.speedometerIconContainer}><CompetencyIcon /></View>
                                                            <Speedometer
                                                                score={supervisorReport.scoring_breakdown.uncapped_competency_pct || supervisorReport.scoring_breakdown.competency_score || 0}
                                                                needleScore={supervisorReport.scoring_breakdown.uncapped_competency_pct || supervisorReport.scoring_breakdown.competency_score}
                                                                size={130}
                                                                thresholdScore={getThresholdScore()}
                                                                label="CLINICAL COMPETENCY"
                                                            />
                                                        </View>
                                                        <View style={[styles.scoreBox, styles.speedometerBox, { marginRight: 10 }]}>
                                                            <View style={styles.speedometerIconContainer}><SafetyIcon /></View>
                                                            <Speedometer
                                                                score={supervisorReport.scoring_breakdown.safety_score || 0}
                                                                size={130}
                                                                thresholdScore={null}
                                                                label="SAFETY COMPLIANCE"
                                                            />
                                                        </View>
                                                        <View style={[styles.scoreBox, styles.speedometerBox]}>
                                                            <View style={styles.speedometerIconContainer}><OptimizationIcon /></View>
                                                            <Speedometer
                                                                score={supervisorReport.scoring_breakdown.optimization_score || 0}
                                                                size={130}
                                                                thresholdScore={null}
                                                                label="CLINICAL OPTIMIZATION"
                                                            />
                                                        </View>
                                                    </>
                                                );
                                            })()}
                                        </View>
                                    )}

                                    {/* SI Follow-Up Status Banner */}
                                    {(() => {
                                        const sfs = supervisorReport?.safety_followup_status;
                                        if (!sfs || sfs.status === 'NOT_APPLICABLE') return null;
                                        const statusColors = {
                                            CURRENT:  { bg: '#ECFDF5', border: '#6EE7B7', badge: '#059669', text: '#065F46' },
                                            DUE:      { bg: '#FFFBEB', border: '#FDE68A', badge: '#D97706', text: '#78350F' },
                                            OVERDUE:  { bg: '#FEF2F2', border: '#FECACA', badge: '#DC2626', text: '#7F1D1D' },
                                            EXPIRED:  { bg: '#EFF6FF', border: '#DC2626', badge: '#2563EB', text: '#1E3A5F' },
                                        };
                                        const c = statusColors[sfs.status] || statusColors.OVERDUE;
                                        const badgeLabel = sfs.status === 'EXPIRED' ? 'SI CLOSED' : `SI ${sfs.status}`;
                                        const bannerTitle = sfs.status === 'EXPIRED'
                                            ? 'Suicidal Ideation — Final Notice'
                                            : 'Suicidal Ideation Follow-Up';
                                        return (
                                            <View wrap={false} style={{
                                                marginTop: 8,
                                                padding: 10,
                                                backgroundColor: c.bg,
                                                borderRadius: 8,
                                                borderWidth: 1,
                                                borderColor: c.border,
                                                borderLeftWidth: 4,
                                                borderLeftColor: c.badge,
                                                flexDirection: 'row',
                                                alignItems: 'flex-start',
                                                gap: 10,
                                            }}>
                                                <View style={{
                                                    backgroundColor: c.badge,
                                                    borderRadius: 4,
                                                    paddingHorizontal: 8,
                                                    paddingVertical: 3,
                                                    minWidth: 70,
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <Text style={{ fontSize: 8, color: '#FFFFFF', fontFamily: 'Helvetica-Bold', letterSpacing: 0.5 }}>
                                                        {badgeLabel}
                                                    </Text>
                                                </View>
                                                <View style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: c.text, marginBottom: 2 }}>
                                                        {bannerTitle}
                                                    </Text>
                                                    <Text style={{ fontSize: 8, color: c.text, lineHeight: 1.4 }}>
                                                        {sfs.note}
                                                    </Text>
                                                </View>
                                            </View>
                                        );
                                    })()}
                                </View>
                            )}

                            {/* --- SUPERVISOR BRIEF CARD --- */}
                            {supervisorReport && (
                                <View style={styles.section}>
                                    <View style={{ marginBottom: 5 }}>
                                        <View wrap={true} style={{
                                            paddingLeft: 20,
                                            paddingRight: 20,
                                            paddingTop: 10,
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: 4,
                                            borderWidth: 1,
                                            borderColor: '#E5E7EB',
                                            paddingBottom: 5,
                                        }}>
                                            {/* --- SUPERVISOR BRIEF SECTION --- */}
                                            <View style={{ marginBottom: 10, borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingBottom: 5 }}>
                                                <Text style={{ fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#111827', textTransform: 'uppercase' }}>SUPERVISOR BRIEF</Text>
                                            </View>

                                            {/* Executive Summary */}
                                            {supervisorReport.executive_summary && (
                                                <View wrap={false} style={{ marginBottom: 20 }}>
                                                    <Text style={[styles.cardTitle, { fontSize: 11, marginBottom: 8, color: '#374151' }]}>EXECUTIVE SUMMARY</Text>
                                                    <Text style={[styles.textBody, { fontSize: 10, lineHeight: 1.5, color: '#374151' }]}>
                                                        {supervisorReport.executive_summary.replace(/Summary[:\s-]*/i, "").split(/\**EXPERT CLINICAL SUPERVISION ANALYSIS:?\**/i)[0].trim()}
                                                    </Text>
                                                </View>
                                            )}

                                            {/* Stacked: Strengths & Improvements */}
                                            <View style={{ flexDirection: 'column', marginTop: 5 }}>
                                                {(() => {
                                                    let strengths = [];
                                                    if (supervisorReport.counsellor_interventions_and_skills) {
                                                        strengths = Object.values(supervisorReport.counsellor_interventions_and_skills)
                                                            .filter(s => s.score >= (scaleMax * 0.67) && s.positive_feedback)
                                                            .sort((a, b) => b.score - a.score)
                                                            .map(s => s.positive_feedback)
                                                            .slice(0, 3);
                                                    }
                                                    if (!strengths.length && supervisorReport.key_strengths) {
                                                        strengths = supervisorReport.key_strengths.slice(0, 3);
                                                    }
                                                    const hasContent = strengths.length > 0;

                                                    return (
                                                        <View wrap={false} style={styles.styledBoxContainer}>
                                                            <View style={[styles.styledBoxShadow, styles.styledBoxShadowGreen]} />
                                                            <View style={[
                                                                styles.styledBox,
                                                                styles.styledBoxGreen,
                                                                styles.styledBoxPadding,
                                                                hasContent ? styles.styledBoxPaddingBottom : styles.styledBoxPaddingBottomSmall
                                                            ]}>
                                                                <View style={[
                                                                    styles.styledBoxHeader,
                                                                    hasContent ? styles.styledBoxHeaderWithContent : styles.styledBoxHeaderNoContent
                                                                ]}>
                                                                    <View style={[styles.styledBoxAccentBar, styles.styledBoxAccentBarGreen]} />
                                                                    <Text style={styles.styledBoxTitle}>{strengths.length > 1 ? `TOP ${strengths.length} OBSERVATIONS & STRENGTHS` : (strengths.length === 1 ? 'KEY OBSERVATION & STRENGTH' : 'OBSERVATIONS & STRENGTHS')}</Text>
                                                                </View>
                                                                {hasContent && (
                                                                    <View style={styles.styledBoxContent}>
                                                                        {strengths.map((str, i) => (
                                                                            <View key={i} style={styles.styledBoxItem}>
                                                                                <View style={[styles.styledBoxBullet, styles.styledBoxBulletGreen]} />
                                                                                <Text style={styles.styledBoxText}>{str}</Text>
                                                                            </View>
                                                                        ))}
                                                                    </View>
                                                                )}
                                                            </View>
                                                        </View>
                                                    );
                                                })()}

                                                {(() => {
                                                    let areas = [];
                                                    if (supervisorReport.counsellor_interventions_and_skills) {
                                                        areas = Object.values(supervisorReport.counsellor_interventions_and_skills)
                                                            .filter(s => s.score <= 3 && s.negative_feedback && !s.optional)
                                                            .sort((a, b) => (a.score || 0) - (b.score || 0))
                                                            .map(s => s.negative_feedback)
                                                            .slice(0, 3);
                                                    }
                                                    if (!areas.length && (supervisorReport.key_weaknesses || supervisorReport.areas_for_improvement)) {
                                                        areas = (supervisorReport.key_weaknesses || supervisorReport.areas_for_improvement).slice(0, 3);
                                                    }
                                                    const hasContent = areas.length > 0;

                                                    return (
                                                        <View wrap={false} style={styles.styledBoxContainer}>
                                                            <View style={[styles.styledBoxShadow, styles.styledBoxShadowPink]} />
                                                            <View style={[
                                                                styles.styledBox,
                                                                styles.styledBoxPink,
                                                                styles.styledBoxPadding,
                                                                hasContent ? styles.styledBoxPaddingBottom : styles.styledBoxPaddingBottomSmall
                                                            ]}>
                                                                <View style={[
                                                                    styles.styledBoxHeader,
                                                                    hasContent ? styles.styledBoxHeaderWithContent : styles.styledBoxHeaderNoContent
                                                                ]}>
                                                                    <View style={[styles.styledBoxAccentBar, styles.styledBoxAccentBarRed]} />
                                                                    <Text style={styles.styledBoxTitle}>{areas.length > 1 ? `TOP ${areas.length} OPTIMIZATION OPPORTUNITIES` : (areas.length === 1 ? 'KEY OPTIMIZATION OPPORTUNITY' : 'OPTIMIZATION OPPORTUNITIES')}</Text>
                                                                </View>
                                                                {hasContent && (
                                                                    <View style={styles.styledBoxContent}>
                                                                        {areas.map((area, i) => (
                                                                            <View key={i} style={styles.styledBoxItem}>
                                                                                <View style={[styles.styledBoxBullet, styles.styledBoxBulletRed]} />
                                                                                <Text style={styles.styledBoxText}>{area}</Text>
                                                                            </View>
                                                                        ))}
                                                                    </View>
                                                                )}
                                                            </View>
                                                        </View>
                                                    );
                                                })()}
                                            </View>
                                        </View>
                                    </View>
                                </View>
                            )}

                            {/* --- CONSULTATION REVIEW (Supervisor's Comments) --- */}
                            {consultationData.review_comment && (
                                <View style={styles.section} break={false}>
                                    <View style={styles.sectionHeader}>
                                        <Text style={styles.sectionTitle}>Manual Consultation Review</Text>
                                    </View>
                                    <View style={[styles.card, { borderLeftWidth: 4, borderLeftColor: theme.secondary }]}>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <Text style={[styles.cardTitle, { color: theme.secondary }]}>SUPERVISOR FEEDBACK</Text>
                                            <Text style={{ fontSize: 8, color: theme.textLight }}>
                                                Reviewed On: {consultationData.reviewed_on ? new Date(consultationData.reviewed_on).toLocaleDateString() : 'N/A'}
                                            </Text>
                                        </View>
                                        <Text style={styles.textBody}>
                                            {consultationData.review_comment}
                                        </Text>
                                    </View>
                                </View>
                            )}

                            {/* --- DETAILED CLINICAL ANALYSIS (Pages 2+) --- */}
                            {supervisorReport && (() => {
                                if (!supervisorReport) return null;

                                // 1. Snapshot
                                const turnCount = supervisorReport.turn_count
                                    || supervisorReport.raw_signals?.meta?.turn_count
                                    || (supervisorReport.raw_signals && typeof supervisorReport.raw_signals === 'string' ? JSON.parse(supervisorReport.raw_signals).meta?.turn_count : 0)
                                    || 45;

                                // Attendee(s) are auto-inferred from self-referential language in the
                                // transcript (husband_present/wife_present), NOT a verified fact — the
                                // underlying signal can misfire, so this is displayed as a soft hint
                                // ("Detected:") rather than an assertion, and only shown when the
                                // session actually involves partner/family-role content.
                                const attendeesDetected = supervisorReport.scoring_breakdown?.attendees_detected || [];
                                const attendeeChangeDetected = supervisorReport.scoring_breakdown?.attendee_change_detected || false;
                                // Only surface the attendee line when it's actually informative — a
                                // genuine change from the prior session. Repeating an unchanged guess
                                // every report (including the first session, where there's nothing to
                                // compare against yet) adds no value and repeats the "unverified LLM
                                // inference" risk for no reason.
                                const showAttendeeLine = attendeesDetected.length > 0 && attendeeChangeDetected;

                                const snapshot = {
                                    condition: supervisorReport.clinical_diagnosis?.condition || "Unknown",
                                    phase: supervisorReport.scoring_breakdown?.session_phase || "Unknown",
                                    risk_level: (supervisorReport.safety_concerns && supervisorReport.safety_concerns.length > 0) ? "Review Required" : "Clinically Safe",
                                    engagement: supervisorReport.resistanceorengagement || "High",
                                    alliance: supervisorReport.therapeutic_alliance || "Good",
                                    length: `${Math.round(turnCount * 1.2)} min (est)`,
                                    turn_count: turnCount,
                                    attendees_detected: attendeesDetected,
                                    attendee_change_detected: attendeeChangeDetected,
                                    show_attendee_line: showAttendeeLine
                                };

                                // 2. Heatmap
                                // [DYNAMIC REFACTOR] Instead of hardcoded CBT list, we use the active skills present in the report.
                                const standardCBTKeys = [
                                    "agenda_setting", "feedback", "collaboration", "pacing",
                                    "interpersonal_effectiveness", "eliciting_emotion", "eliciting_cognitions",
                                    "behavioral_interventions", "guided_discovery", "conceptual_integration",
                                    "change_methods", "homework"
                                ];

                                const COMPETENCY_NAMES = {
                                    "agenda_setting": "Agenda Setting",
                                    "pacing": "Pacing & Time Use",
                                    "feedback": "Feedback",
                                    "collaboration": "Collaboration",
                                    "interpersonal_effectiveness": "Interpersonal Effectiveness",
                                    "eliciting_emotion": "Eliciting Emotion",
                                    "eliciting_cognitions": "Eliciting Cognitions",
                                    "guided_discovery": "Guided Discovery",
                                    "conceptual_integration": "Conceptual Integration",
                                    "behavioral_interventions": "Behavioral Interventions",
                                    "change_methods": "Change Methods",
                                    "homework": "Homework Setting",
                                    "active_listening": "Active Listening",
                                    // NIMHANS Mapping
                                    "nh_session_structure_rapport": "Session Structure & Rapport",
                                    "nh_assessment_exploration": "Assessment & Exploration",
                                    "nh_therapeutic_presence": "Therapeutic Presence",
                                    "nh_case_formulation": "Case Formulation",
                                    "nh_intervention_relevance": "Intervention Relevance",
                                    "nh_goal_setting_action_planning": "Goal & Action Planning",
                                    "nh_time_management": "Time Management",
                                    "nh_termination_continuity": "Termination & Continuity",
                                    "nh_ethical_professionalism": "Ethics & Professionalism",
                                    "nh_risk_safety": "Risk & Safety Screening"
                                };

                                // Decide which keys to show in the global heatmap.
                                const heatmapKeys = skillKeys.length > 0 ? skillKeys : standardCBTKeys;

                                const heatmap = heatmapKeys.map(k => {
                                    const skillData = skills[k] || {};

                                    // Skip if explicitly hidden
                                    if (skillData.hideFromPDF) return null;

                                    // # 2. Fallback to native (0-6) or legacy linear translation if needed
                                    // [NEW] Snap to nearest 0.5 increment as per clinical standard
                                    const snapScore = Math.round((skillData.score || 0) * 2) / 2;

                                    // Fallback name formatting if name is missing
                                    const rawName = skillData.name || COMPETENCY_NAMES[k] || k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

                                    // Check optional status
                                    const isOptional = skillData.optional === true || skillData.feedback_type === 'Optional';

                                    return {
                                        name: rawName + (isOptional && skillData.score ? ' - Opt' : ''),
                                        score: skillData.score || 0
                                    };
                                }).filter(Boolean);

                                // 3. Top Strengths
                                let topStrengths = skillKeys
                                    .filter(k => skills[k].score >= (scaleMax * 0.67))
                                    .map(k => skills[k].positive_feedback || skills[k].specific_observations)
                                    .filter(Boolean)
                                    .slice(0, 3);
                                if (topStrengths.length === 0 && supervisorReport.key_strengths) {
                                    topStrengths = supervisorReport.key_strengths.slice(0, 3);
                                }

                                // 4. Critical Areas — only mandatory competencies that scored low
                                let critical = skillKeys.filter(k => {
                                    const s = skills[k];
                                    const isMandatory = s.optional !== true && s.feedback_type !== 'Optional';
                                    return isMandatory && (s.score === 0 || s.score < (satisfactoryThreshold * 0.67));
                                }).map(k => {
                                    const s = skills[k];
                                    if (s.score === 0) return `${s.name}: Scored 0 (Absent)`;
                                    return `${s.name}: Scored ${s.score} (Deficit)`;
                                }).slice(0, 4);

                                // 5. Missed Ops
                                let missed = [];
                                skillKeys.forEach(k => {
                                    if (skills[k].missed_opportunities) missed.push(...skills[k].missed_opportunities);
                                    if (skills[k].depth_clarity) missed.push(...skills[k].depth_clarity.map(d => d.refinement));
                                });
                                missed = missed.slice(0, 3);

                                // 6. Concerns & Targets
                                const concerns = supervisorReport.review_meta?.review_reasons || ["None"];

                                // Helper to format concern text
                                const formatConcernText = (text) => {
                                    if (!text) return "";
                                    let final = text;
                                    // Combine known keys and likely keys
                                    const keysToFormat = Array.from(new Set([...(typeof standardCBTKeys !== 'undefined' ? standardCBTKeys : []), ...(typeof skillKeys !== 'undefined' ? skillKeys : [])]));

                                    keysToFormat.forEach(k => {
                                        if (!k) return;
                                        const pattern = new RegExp(`\\b${k}\\b`, 'gi');
                                        final = final.replace(pattern, () => {
                                            // Use specific mapping if available, otherwise fallback to capitalization logic
                                            const keyLower = k.toLowerCase();
                                            if (COMPETENCY_NAMES[keyLower]) {
                                                return COMPETENCY_NAMES[keyLower];
                                            }
                                            return k.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                                        });
                                    });
                                    return final.replace(/_/g, ' ');
                                };

                                let targets = [];
                                skillKeys.filter(k => {
                                    const s = skills[k];
                                    // Development targets: only mandatory competencies that scored low
                                    const isMandatory = s.optional !== true && s.feedback_type !== 'Optional';
                                    // Dynamic threshold check
                                    return isMandatory && (s.score === undefined || s.score < satisfactoryThreshold);
                                }).forEach(k => {
                                    const s = skills[k];
                                    const step = s.curated_action_step || s.suggested_phrasing;
                                    if (step) targets.push(step);
                                });
                                targets = targets.slice(0, 4);

                                // 8. Reading
                                const reading = supervisorReport.recommended_reading ? supervisorReport.recommended_reading.slice(0, 3) : [];

                                // --- HELPER: STYLED BOX ---
                                const StyledBox = ({ title, colorTheme, children, icon, style, autoHeight }) => {
                                    // Define theme colors
                                    const colors = {
                                        blue: { bg: '#EFF6FF', border: '#BFDBFE', accent: '#3B82F6', text: '#1E40AF', shadow: '#DBEAFE' },
                                        amber: { bg: '#FFFBEB', border: '#FDE68A', accent: '#F59E0B', text: '#92400E', shadow: '#FEF3C7' },
                                        green: { bg: '#ECFDF5', border: '#D1FAE5', accent: '#10B981', text: '#065F46', shadow: '#D1FAE5' },
                                        red: { bg: '#FEF2F2', border: '#FECACA', accent: '#EF4444', text: '#991B1B', shadow: '#FEE2E2' },
                                        teal: { bg: '#F0FDFA', border: '#CCFBF1', accent: '#14B8A6', text: '#115E59', shadow: '#E0F2F1' },
                                        gray: { bg: '#F9FAFB', border: '#E5E7EB', accent: '#6B7280', text: '#374151', shadow: '#F3F4F6' },
                                        indigo: { bg: '#EEF2FF', border: '#C7D2FE', accent: '#6366F1', text: '#3730A3', shadow: '#E0E7FF' }
                                    }[colorTheme] || colors.gray;

                                    // Layout Logic: Auto height vs Flex fill
                                    const containerStyle = autoHeight
                                        ? { marginBottom: 0, position: 'relative' }
                                        : { flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' };

                                    const cardStyle = autoHeight
                                        ? { backgroundColor: colors.bg, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: 12 }
                                        : { backgroundColor: colors.bg, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: 12, flexGrow: 1 };

                                    return (
                                        <View wrap={false} style={[containerStyle, style]}>
                                            {/* Shadow */}
                                            <View style={{ position: 'absolute', top: 3, left: 3, width: '100%', height: '100%', backgroundColor: colors.shadow, borderRadius: 12 }} />
                                            {/* Main Card */}
                                            <View style={cardStyle}>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10, paddingBottom: 6, borderBottomWidth: 1, borderBottomColor: colors.border, borderStyle: 'dashed' }}>
                                                    <View style={{ width: 4, height: 14, backgroundColor: colors.accent, marginRight: 8, borderRadius: 2 }} />
                                                    <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: colors.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        {icon} {title}
                                                    </Text>
                                                </View>
                                                <View style={{ gap: 4 }}>
                                                    {children}
                                                </View>
                                            </View>
                                        </View>
                                    );
                                };

                                return (
                                    <View style={{ marginTop: 25, paddingTop: 15, borderTopWidth: 2, borderTopColor: '#F3F4F6' }}>
                                        {/* Header */}
                                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', paddingBottom: 6 }}>
                                            <View>
                                                <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#111827', textTransform: 'uppercase' }}>DETAILED CLINICAL ANALYSIS</Text>
                                            </View>
                                        </View>

                                        {/* Section 1 & 2: Snapshot & Heatmap */}
                                        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                            <StyledBox title="Session Snapshot" colorTheme="blue" icon="📋">
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}><Text style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 'bold', width: '35%' }}>Condition:</Text><Text style={{ fontSize: 9, color: '#374151', width: '65%', textAlign: 'right' }}>{String(snapshot.condition || "N/A")}</Text></View>
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 'bold' }}>Phase:</Text><Text style={{ fontSize: 9, color: '#374151' }}>{snapshot.phase}</Text></View>
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 'bold' }}>Risk Level:</Text><Text style={{ fontSize: 9, color: snapshot.risk_level === 'Clinically Safe' ? '#059669' : '#DC2626', fontWeight: 'bold' }}>{snapshot.risk_level}</Text></View>
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 'bold' }}>Engagement:</Text><Text style={{ fontSize: 9, color: '#374151' }}>{snapshot.engagement}</Text></View>
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 'bold' }}>Alliance:</Text><Text style={{ fontSize: 9, color: '#374151' }}>{snapshot.alliance}</Text></View>
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 'bold' }}>Length/Turns:</Text><Text style={{ fontSize: 9, color: '#374151' }}>{snapshot.length} / {snapshot.turn_count}</Text></View>
                                                {snapshot.show_attendee_line && (
                                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                                        <Text style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 'bold' }}>Attendee(s):</Text>
                                                        <Text style={{ fontSize: 9, color: '#6B7280', fontStyle: 'italic' }}>
                                                            Detected: {snapshot.attendees_detected.join(' & ')}
                                                        </Text>
                                                    </View>
                                                )}
                                                {snapshot.attendee_change_detected && (
                                                    <Text style={{ fontSize: 7.5, color: '#92400E', fontStyle: 'italic', marginTop: 2 }}>
                                                        Note: auto-detected attendee(s) differ from the previous session — unverified, please confirm.
                                                    </Text>
                                                )}
                                            </StyledBox>

                                            <StyledBox title="Competency Heatmap" colorTheme="amber" icon="🔥">
                                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                                                    {heatmap.slice(0, 12).map((h, i) => (
                                                        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: 2 }}>
                                                            <View style={{
                                                                width: 8, height: 8, borderRadius: 4, marginRight: 6,
                                                                backgroundColor: h.score >= satisfactoryThreshold ? '#10B981' : h.score < (satisfactoryThreshold * 0.67) ? '#EF4444' : '#F59E0B'
                                                            }} />
                                                            <Text style={{ fontSize: 8, color: '#78350F', flex: 1 }} numberOfLines={1}>{h.name}</Text>
                                                            <Text style={{ fontSize: 8, color: '#92400E', fontWeight: 'bold' }}>{h.score}</Text>
                                                        </View>
                                                    ))}
                                                </View>
                                            </StyledBox>
                                        </View>

                                        {/* --- RECURRING PATTERNS (Feature 3) --- */}
                                        {(() => {
                                            const rp = supervisorReport?.recurring_patterns;
                                            if (!rp) return null;
                                            // Only show patterns for competencies present in the heatmap
                                            const heatmapKeys = new Set(Object.keys(supervisorReport?.counsellor_interventions_and_skills || {}));
                                            const filteredDeficits = (rp.persistent_deficits || []).filter(d => heatmapKeys.has(d.competency));
                                            const filteredInconsistent = (rp.inconsistent_deployment || []).filter(d => heatmapKeys.has(d.competency));
                                            const filteredImproving = (rp.improving || []).filter(d => heatmapKeys.has(d.competency));
                                            const filteredNewlyFlagged = (rp.newly_flagged || []).filter(d => heatmapKeys.has(d.competency));
                                            const hasContent = (
                                                filteredDeficits.length > 0 ||
                                                filteredInconsistent.length > 0 ||
                                                filteredImproving.length > 0 ||
                                                filteredNewlyFlagged.length > 0 ||
                                                rp.homework_review_absent_streak > 0
                                            );
                                            if (!hasContent) return null;
                                            return (
                                                <View wrap={false} style={{ marginBottom: 10 }}>
                                                    <StyledBox title={`Recurring Patterns (${rp.sessions_analysed} Sessions)`} colorTheme="gray" icon="📊" autoHeight>
                                                        {filteredDeficits.length > 0 && (
                                                            <View style={{ marginBottom: 6 }}>
                                                                <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#DC2626', marginBottom: 3 }}>⚠ Persistent Deficits</Text>
                                                                {filteredDeficits.map((d, i) => (
                                                                    <View key={`pd-${i}`} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                                                        <Text style={{ fontSize: 8, color: '#DC2626', marginRight: 4 }}>•</Text>
                                                                        <Text style={{ fontSize: 8, color: '#7F1D1D', flex: 1 }}>
                                                                            {d.name} — below threshold for {d.consecutive_sessions} consecutive sessions (latest: {d.current_score})
                                                                        </Text>
                                                                    </View>
                                                                ))}
                                                            </View>
                                                        )}
                                                        {filteredInconsistent.length > 0 && (
                                                            <View style={{ marginBottom: 6 }}>
                                                                <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#7C3AED', marginBottom: 3 }}>~ Inconsistent Deployment</Text>
                                                                {filteredInconsistent.map((d, i) => (
                                                                    <View key={`id-${i}`} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                                                        <Text style={{ fontSize: 8, color: '#7C3AED', marginRight: 4 }}>•</Text>
                                                                        <Text style={{ fontSize: 8, color: '#4C1D95', flex: 1 }}>
                                                                            {d.name} — skill present, deployment inconsistent (demonstrated in {d.sessions_above_threshold} prior session{d.sessions_above_threshold !== 1 ? 's' : ''}, latest: {d.current_score})
                                                                        </Text>
                                                                    </View>
                                                                ))}
                                                            </View>
                                                        )}
                                                        {filteredImproving.length > 0 && (
                                                            <View style={{ marginBottom: 6 }}>
                                                                <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#059669', marginBottom: 3 }}>↑ Improving</Text>
                                                                {filteredImproving.map((d, i) => (
                                                                    <View key={`im-${i}`} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                                                        <Text style={{ fontSize: 8, color: '#059669', marginRight: 4 }}>•</Text>
                                                                        <Text style={{ fontSize: 8, color: '#065F46', flex: 1 }}>
                                                                            {d.name} crossed threshold (now: {d.current_score})
                                                                        </Text>
                                                                    </View>
                                                                ))}
                                                            </View>
                                                        )}
                                                        {filteredNewlyFlagged.length > 0 && (
                                                            <View style={{ marginBottom: 6 }}>
                                                                <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#D97706', marginBottom: 3 }}>↓ Newly Flagged</Text>
                                                                {filteredNewlyFlagged.map((d, i) => (
                                                                    <View key={`nf-${i}`} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                                                        <Text style={{ fontSize: 8, color: '#D97706', marginRight: 4 }}>•</Text>
                                                                        <Text style={{ fontSize: 8, color: '#78350F', flex: 1 }}>
                                                                            {d.name} dropped below threshold this session (score: {d.current_score})
                                                                        </Text>
                                                                    </View>
                                                                ))}
                                                            </View>
                                                        )}
                                                        {rp.homework_review_absent_streak > 0 && (
                                                            <View style={{ paddingTop: 5, borderTopWidth: 1, borderTopColor: '#E5E7EB', borderStyle: 'dashed' }}>
                                                                <Text style={{ fontSize: 8, color: '#6B7280' }}>
                                                                    📋 Homework review has been absent for {rp.homework_review_absent_streak} consecutive session(s).
                                                                </Text>
                                                            </View>
                                                        )}
                                                    </StyledBox>
                                                </View>
                                            );
                                        })()}

                                        {/* Section 3 & 4: Strengths & Critical */}
                                        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                            {/* Top Strengths removed as redundant */}
                                            <StyledBox title="Critical Areas" colorTheme="red" icon="🚨">
                                                {critical.length > 0 ? critical.map((c, i) => (
                                                    <View key={i} style={{ flexDirection: 'row', marginBottom: 4 }}>
                                                        <Text style={{ fontSize: 8, color: '#DC2626', marginRight: 4 }}>•</Text>
                                                        <Text style={{ fontSize: 9, color: '#7F1D1D', lineHeight: 1.3 }}>{c}</Text>
                                                    </View>
                                                )) : <Text style={{ fontSize: 9, color: '#9CA3AF', fontStyle: 'italic' }}>No critical deficits detected.</Text>}

                                                {/* Security Concerns */}
                                                {supervisorReport.safety_concerns && supervisorReport.safety_concerns.length > 0 && (
                                                    <View style={{ marginTop: 8, paddingTop: 6, borderTopWidth: 1, borderTopColor: '#FECACA', borderStyle: 'dashed' }}>
                                                        <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#B91C1C', marginBottom: 4 }}>Security Concern(s)</Text>
                                                        {supervisorReport.safety_concerns.map((concern, i) => (
                                                            <View key={`safe-${i}`} style={{ flexDirection: 'row', marginBottom: 4 }}>
                                                                <Text style={{ fontSize: 8, color: '#DC2626', marginRight: 4 }}>•</Text>
                                                                <Text style={{ fontSize: 9, color: '#7F1D1D', lineHeight: 1.3 }}>{concern}</Text>
                                                            </View>
                                                        ))}
                                                    </View>
                                                )}
                                            </StyledBox>
                                        </View>

                                        {/* Section 5: Missed Opportunities */}
                                        {missed.length > 0 && (
                                            <View style={{ marginBottom: 10 }}>
                                                <StyledBox title="High-Impact Missed Opportunities" colorTheme="red" icon="🎯" autoHeight>
                                                    {missed.map((m, i) => (
                                                        <View key={i} style={{ flexDirection: 'row', marginBottom: 4 }}>
                                                            <Text style={{ fontSize: 8, color: '#DC2626', marginRight: 4 }}>•</Text>
                                                            <Text style={{ fontSize: 9, color: '#7F1D1D' }}>{m}</Text>
                                                        </View>
                                                    ))}
                                                </StyledBox>
                                            </View>
                                        )}

                                        {/* Section 6, 7 & 8: Development Targets (Col 1) | Concerns + Reading (Col 2) */}
                                        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                            {/* Col 1: Development Targets */}
                                            <StyledBox title="Development Targets" colorTheme="indigo" icon="⚡" style={{ flex: 1 }}>
                                                {targets.map((t, i) => (
                                                    <View key={i} style={{ flexDirection: 'row', marginBottom: 3 }}>
                                                        <Text style={{ fontSize: 9, color: '#312E81', marginRight: 5 }}>•</Text>
                                                        <Text style={{ fontSize: 9, color: '#312E81', flex: 1, lineHeight: 1.3 }}>{t}</Text>
                                                    </View>
                                                ))}
                                            </StyledBox>

                                            {/* Col 2: Concerns & Reading */}
                                            <View style={{ flex: 1, gap: 15 }}>
                                                {/* Dynamic Theme for Concerns */}
                                                {(() => {
                                                    const isCritical = supervisorReport.review_meta?.needs_manual_review;
                                                    const theme = isCritical ? 'red' : 'gray';
                                                    const icon = isCritical ? "🚨" : "🛡️";

                                                    return (
                                                        <StyledBox title="Supervisor Concerns" colorTheme={theme} icon={icon} autoHeight>
                                                            {concerns.length > 0 && concerns[0] !== "None" ? concerns.map((c, i) => (
                                                                <View key={i} style={{ flexDirection: 'row', marginBottom: 3 }}>
                                                                    <Text style={{ fontSize: 9, color: isCritical ? '#7F1D1D' : '#374151', marginRight: 5 }}>•</Text>
                                                                    <Text style={{ fontSize: 9, color: isCritical ? '#7F1D1D' : '#374151', flex: 1, lineHeight: 1.3 }}>{formatConcernText(c)}</Text>
                                                                </View>
                                                            )) : <Text style={{ fontSize: 9, color: '#9CA3AF', fontStyle: 'italic' }}>No concerns noted.</Text>}
                                                        </StyledBox>
                                                    );
                                                })()}

                                                {/* Removed Rec. Reading section */}
                                            </View>
                                        </View>

                                    </View>
                                );
                            })()}

                            {/* --- PATIENT CONTEXT --- */}
                            {(getBioNarrative() !== "N/A" || consultationData?.is_transfer_case || consultationData?.previous_homework_assigned) && (
                                <View style={[styles.section, { marginTop: 20 }]}>
                                    <View style={styles.sectionHeader}>
                                        <Text style={styles.sectionTitle}>Patient Context & Clinical Flow </Text>
                                    </View>

                                    {/* Transfer Case & Homework Alert */}
                                    {/* {(consultationData?.is_transfer_case || consultationData?.previous_homework_assigned) && (
                                        <View style={{ marginBottom: 10, padding: 10, backgroundColor: '#FEF2F2', borderRadius: 8, borderWidth: 1, borderColor: '#FECACA' }}>
                                            {consultationData?.is_transfer_case && (
                                                <Text style={{ fontSize: 10, color: '#991B1B', fontFamily: 'Helvetica-Bold', marginBottom: consultationData?.previous_homework_assigned ? 4 : 0 }}>
                                                    TRANSFER CASE: Patient was previously seen by {consultationData?.previous_doctor_name || "another therapist"}.
                                                </Text>
                                            )}
                                            {consultationData?.previous_homework_assigned && (
                                                <Text style={{ fontSize: 10, color: '#92400E', fontFamily: 'Helvetica-Bold' }}>
                                                    HOMEWORK FOLLOW-UP: {consultationData?.previous_homework_text ? `Homework was assigned: ${consultationData.previous_homework_text}` : "Homework was assigned in the previous session. Please review."}
                                                </Text>
                                            )}
                                        </View>
                                    )} */}

                                    {/* Presenting Condition moved to Page 1 */}

                                    {/* Bio */}
                                    {getBioNarrative() !== getPresentingCondition() && (
                                        <View style={{ flexDirection: 'row', gap: 10 }}>
                                            <View style={{ flex: 1 }}>
                                                <Text style={styles.cardTitle}>
                                                    {supervisorReport?.previous_session_hypothesis ? 'Last Session Summary' : 'Patient Background'}
                                                </Text>
                                                <Text style={styles.textBody}>{getBioNarrative()}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {patientDetails?.medical_history && (
                                        <View style={{ marginTop: 10 }}>
                                            <Text style={styles.cardTitle}>Medical History</Text>
                                            <Text style={styles.textBody}>{patientDetails.medical_history}</Text>
                                        </View>
                                    )}
                                </View>
                            )}

                            {/* --- LAST SESSION HANDOFF (Feature 1) --- */}
                            {supervisorReport?.session_handoff && (() => {
                                const sh = supervisorReport.session_handoff;
                                return (
                                    <View style={[styles.section, { marginTop: 20 }]}>
                                        <View style={styles.sectionHeader}>
                                            <Text style={styles.sectionTitle}>Last Session Handoff</Text>
                                        </View>
                                        <View style={{
                                            backgroundColor: '#F9FAFB',
                                            borderRadius: 10,
                                            borderWidth: 1,
                                            borderColor: '#E5E7EB',
                                            padding: 14,
                                            gap: 10,
                                        }}>
                                            {/* Session Focus */}
                                            {sh.session_focus ? (
                                                <View style={{ borderLeftWidth: 3, borderLeftColor: '#6366F1', paddingLeft: 10 }}>
                                                    <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#3730A3', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>Session Focus</Text>
                                                    <Text style={{ fontSize: 9, color: '#374151', lineHeight: 1.4 }}>{sh.session_focus}</Text>
                                                </View>
                                            ) : null}

                                            {/* Homework Assigned */}
                                            {sh.homework_assigned && sh.homework_assigned.length > 0 && (
                                                <View wrap={false} style={{ borderLeftWidth: 3, borderLeftColor: '#10B981', paddingLeft: 10, marginTop: 4, marginBottom: 4 }}>
                                                    <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#065F46', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>Homework Assigned</Text>
                                                    {sh.homework_assigned.map((hw, i) => (
                                                        <View wrap={false} key={`hw-${i}`} style={{ flexDirection: 'row', marginBottom: 3, alignItems: 'flex-start' }}>
                                                            <Text style={{ fontSize: 8, color: '#059669', marginRight: 4, marginTop: 1 }}>•</Text>
                                                            <Text style={{ fontSize: 9, color: '#065F46', flex: 1, lineHeight: 1.4 }}>{hw}</Text>
                                                        </View>
                                                    ))}
                                                </View>
                                            )}

                                            {/* Unresolved Issues */}
                                            {sh.flagged_unresolved && sh.flagged_unresolved.length > 0 && (
                                                <View style={{ borderLeftWidth: 3, borderLeftColor: '#EF4444', paddingLeft: 10 }}>
                                                    <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#991B1B', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>Unresolved Issues</Text>
                                                    {sh.flagged_unresolved.map((item, i) => (
                                                        <View key={`ur-${i}`} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                                            <Text style={{ fontSize: 8, color: '#DC2626', marginRight: 4 }}>!</Text>
                                                            <Text style={{ fontSize: 9, color: '#7F1D1D', flex: 1, lineHeight: 1.4 }}>
                                                                {item.description || item}
                                                            </Text>
                                                        </View>
                                                    ))}
                                                </View>
                                            )}

                                            {/* Therapist Commitments */}
                                            {sh.therapist_commitments && sh.therapist_commitments.length > 0 && (
                                                <View style={{ borderLeftWidth: 3, borderLeftColor: '#F59E0B', paddingLeft: 10 }}>
                                                    <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#92400E', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>Therapist Commitments</Text>
                                                    {sh.therapist_commitments.map((tc, i) => {
                                                        const isObj = typeof tc === 'object' && tc !== null;
                                                        const text = isObj ? tc.text : tc;
                                                        const status = isObj ? tc.status : 'UNVERIFIED';

                                                        let label = '';
                                                        let labelColor = '#78350F';
                                                        if (status === 'MET') {
                                                            label = ' [MET]';
                                                            labelColor = '#059669'; // Green
                                                        } else if (status === 'MISSED') {
                                                            label = ' [MISSED]';
                                                            labelColor = '#DC2626'; // Red
                                                        }

                                                        return (
                                                            <View key={`tc-${i}`} style={{ flexDirection: 'row', marginBottom: 2 }}>
                                                                <Text style={{ fontSize: 8, color: '#D97706', marginRight: 4 }}>•</Text>
                                                                <Text style={{ fontSize: 9, color: '#78350F', flex: 1, lineHeight: 1.4 }}>
                                                                    {text}
                                                                    {label && (
                                                                        <Text style={{ fontFamily: 'Helvetica-Bold', color: labelColor }}>
                                                                            {label}
                                                                        </Text>
                                                                    )}
                                                                </Text>
                                                            </View>
                                                        );
                                                    })}
                                                </View>
                                            )}
                                        </View>
                                    </View>
                                );
                            })()}


                            {showDetailedContent && supervisorReport?.counsellor_interventions_and_skills && (
                                <View style={{ marginTop: 25 }} break>
                                    <View style={styles.sectionHeader}>
                                        <Text style={styles.sectionTitle}>Detailed Competency Analysis</Text>
                                    </View>

                                    {(() => {
                                        const renderSkillCard = (key, skill, i) => {
                                            if (skill.hideFromPDF) return null;
                                            const score = skill.score !== undefined ? skill.score : (skill.scaled_score !== undefined ? skill.scaled_score : 0);

                                            // Thresholds based on satisfactory_threshold
                                            const isExpert = score >= (scaleMax * 0.75);
                                            const isProficient = score >= (satisfactoryThreshold);
                                            const isDeficit = score < (satisfactoryThreshold * 0.85); // Allow slight buffer before flagging deficit
                                            const isHighScore = score >= (scaleMax * 0.80);

                                            const headerColor = isExpert ? '#065F46' : isDeficit ? '#991B1B' : '#92400E';
                                            const scoreBg = isExpert ? '#D1FAE5' : isDeficit ? '#FEE2E2' : '#FEF3C7';

                                            const validBenchmarkData = (benchmarkData || []).filter(b => b != null);
                                            const playbookMatch = validBenchmarkData.find(b => b.related_turn_index === skill?.related_turn_index) || {};

                                            const clinicalRationale = skill.why_correct || playbookMatch.why_correct || playbookMatch.rationale;
                                            const suggestedPhrasing = skill.suggested_phrasing || playbookMatch.expert_response;
                                            const altStrategies = skill.alternative_strategies || supervisorReport.alternative_strategies?.[key] || [];

                                            const contextText = getContextForTurn(skill.related_turn_index || playbookMatch.related_turn_index);
                                            const doctorText = conversationEntries[(skill.related_turn_index || playbookMatch.related_turn_index) - 1]?.doctor;

                                            return (
                                                <View key={i} style={styles.competencyItemContainer} wrap={false}>
                                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                                        <Text style={{ fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#1F2937', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1, marginRight: 15 }}>
                                                            {skill.display_name || skill.name || key.replace(/_/g, ' ')}
                                                        </Text>
                                                        <View style={{ backgroundColor: scoreBg, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, flexShrink: 0, minWidth: 65, alignItems: 'center' }}>
                                                            <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: headerColor }}>
                                                                {skill.optional ? "(OPTIONAL)" : `SCORE: ${Math.round(score * 2) / 2}/${scaleMax}`}
                                                            </Text>
                                                        </View>
                                                    </View>

                                                    {(skill.specific_observations || skill.positive_feedback) && (
                                                        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                                            {skill.specific_observations && (
                                                                <View style={{ flex: 1, backgroundColor: '#F8FAFC', borderRadius: 8, padding: 10, borderWidth: 1, borderColor: '#E2E8F0' }}>
                                                                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#475569', marginBottom: 4 }}>OBSERVATIONS</Text>
                                                                    <Text style={{ fontSize: 9, color: '#334155', lineHeight: 1.4 }}>{skill.specific_observations}</Text>
                                                                </View>
                                                            )}
                                                            {skill.positive_feedback && (
                                                                <View style={{ flex: 1, backgroundColor: '#F0FDF4', borderRadius: 8, padding: 10, borderWidth: 1, borderColor: '#DCFCE7' }}>
                                                                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#166534', marginBottom: 4 }}>STRENGTHS</Text>
                                                                    <Text style={{ fontSize: 9, color: '#14532D', lineHeight: 1.4 }}>{skill.positive_feedback}</Text>
                                                                </View>
                                                            )}
                                                        </View>
                                                    )}

                                                    {skill.depth_clarity && skill.depth_clarity.length > 0 && !isHighScore && (
                                                        <View style={{ backgroundColor: '#EFF6FF', borderRadius: 8, padding: 10, marginBottom: 10, borderWidth: 1, borderColor: '#DBEAFE', borderLeftWidth: 3, borderLeftColor: '#3B82F6' }}>
                                                            <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#1E40AF', marginBottom: 4 }}>DEPTH & CLARITY ENHANCEMENT</Text>
                                                            {skill.depth_clarity.map((item, idx) => (
                                                                <View key={idx} style={{ marginBottom: 4 }}>
                                                                    <Text style={{ fontSize: 9, color: '#1E3A8A' }}>{item.refinement}</Text>
                                                                    {item.example && <Text style={{ fontSize: 9, color: '#2563EB', fontStyle: 'italic', paddingLeft: 8 }}>Try: "{item.example}"</Text>}
                                                                </View>
                                                            ))}
                                                        </View>
                                                    )}

                                                    {!skill.optional && !isHighScore && (skill.negative_feedback || skill.missed_opportunities?.length > 0) && (
                                                        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                                            {skill.negative_feedback && (
                                                                <View style={{ flex: 1, backgroundColor: '#FFF7ED', borderRadius: 8, padding: 10, borderWidth: 1, borderColor: '#FFEDD5', borderLeftWidth: 3, borderLeftColor: '#F97316' }}>
                                                                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#9A3412', marginBottom: 4 }}>OPTIMIZATION</Text>
                                                                    <Text style={{ fontSize: 9, color: '#7C2D12', lineHeight: 1.4 }}>{skill.negative_feedback}</Text>
                                                                </View>
                                                            )}
                                                            {skill.missed_opportunities && skill.missed_opportunities.length > 0 && (
                                                                <View style={{ flex: 1, backgroundColor: '#FEF2F2', borderRadius: 8, padding: 10, borderWidth: 1, borderColor: '#FEE2E2', borderLeftWidth: 3, borderLeftColor: '#EF4444' }}>
                                                                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#991B1B', marginBottom: 4 }}>MISSED OPPORTUNITIES</Text>
                                                                    {skill.missed_opportunities.map((opp, idx) => (
                                                                        <Text key={idx} style={{ fontSize: 9, color: '#7F1D1D' }}>• {opp}</Text>
                                                                    ))}
                                                                </View>
                                                            )}
                                                        </View>
                                                    )}

                                                    <View style={{ flexDirection: 'row', gap: 10 }}>
                                                        {(skill.clinical_context || suggestedPhrasing) && (
                                                            <View style={{ flex: 3, gap: 8 }}>
                                                                {skill.clinical_context && (
                                                                    <View style={{ backgroundColor: '#FFFBEB', borderRadius: 8, padding: 10, borderWidth: 1, borderColor: '#FEF3C7' }}>
                                                                        <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#92400E', marginBottom: 2 }}>CLINICAL CONTEXT</Text>
                                                                        <Text style={{ fontSize: 9, color: '#B45309', lineHeight: 1.4 }}>{skill.clinical_context}</Text>
                                                                    </View>
                                                                )}
                                                                {suggestedPhrasing && (
                                                                    <View style={{ backgroundColor: '#ECFDF5', borderRadius: 8, padding: 10, borderWidth: 1, borderColor: '#D1FAE5' }}>
                                                                        <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#065F46', marginBottom: 2 }}>SUGGESTED PHRASING</Text>
                                                                        <Text style={{ fontSize: 9, color: '#047857', fontStyle: 'italic' }}>"{suggestedPhrasing}"</Text>
                                                                    </View>
                                                                )}
                                                            </View>
                                                        )}
                                                        {(() => {
                                                            const hasValidContext = contextText && contextText !== "[Secure Remote Content]" && contextText.trim() !== "..." && contextText.trim() !== "";
                                                            const hasValidDoctor = doctorText && doctorText !== "[Secure Remote Content]" && doctorText.trim() !== "..." && doctorText.trim() !== "";
                                                            if (!hasValidContext && !hasValidDoctor) return null;

                                                            return (
                                                                <View style={{ flex: 2, backgroundColor: '#F1F5F9', borderRadius: 8, padding: 10 }}>
                                                                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#64748B', marginBottom: 4 }}>TRANSCRIPT CONTEXT</Text>
                                                                    {hasValidContext && <Text style={{ fontSize: 8, color: '#475569', fontStyle: 'italic', marginBottom: 4 }}>Patient: "{truncateText(contextText, 100)}"</Text>}
                                                                    {hasValidDoctor && <Text style={{ fontSize: 8, color: '#475569' }}>Therapist: "{truncateText(doctorText, 100)}"</Text>}
                                                                </View>
                                                            );
                                                        })()}
                                                    </View>
                                                </View>
                                            );
                                        };

                                        return Object.entries(skills).map(([key, skill], i) => renderSkillCard(key, skill, i));
                                    })()}
                                </View>
                            )}

                            {/* --- EXPERT ALIGNMENT --- */}
                            {showDetailedContent && topBenchmarksList.length > 0 && (
                                <View break>
                                    <View style={styles.sectionHeader}>
                                        <Text style={styles.sectionTitle}>Expert Alignment (Top Matches)</Text>
                                    </View>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginBottom: 10, fontStyle: 'italic' }}>
                                        Comparison of your responses against verified clinical playbooks for similar patient scenarios.
                                        Uses Semantic Similarity (0-100%) to identify high-quality matches.
                                        <Text style={{ fontWeight: 'bold' }}> Note: This represents the 'Textbook Model Response', not necessarily the only correct way.</Text>
                                    </Text>

                                    {topBenchmarksList.map((bm, i) => {
                                        // Convert confidence (0-1) to similarity score (0-100)
                                        const similarityScore = (bm.confidence || 0) * 100;
                                        return (
                                            <View key={i} style={styles.card}>
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                                                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                                        <Text style={[styles.cardTitle, { color: theme.textLight }]}>TURN #{bm.originalIndex}</Text>
                                                        {similarityScore >= 85 && (
                                                            <Text style={{ marginLeft: 8, fontSize: 8, color: theme.success, fontWeight: 'bold' }}>HIGH ALIGNMENT</Text>
                                                        )}
                                                    </View>
                                                    <Text style={[styles.cardTitle, { color: theme.accent }]}>CLINICAL BENCHMARK</Text>
                                                </View>

                                                {(() => {
                                                    const context = getContextForTurn(bm.originalIndex);
                                                    if (!context || context === "[Secure Remote Content]" || context.trim() === "..." || context.trim() === "") return null;
                                                    return (
                                                        <View style={{ marginBottom: 6 }}>
                                                            <Text style={[styles.textBody, { color: theme.textLight, fontStyle: 'italic' }]}>
                                                                Context: "{truncateText(context, 220)}"
                                                            </Text>
                                                        </View>
                                                    );
                                                })()}

                                                <View>
                                                    <Text style={[styles.textBody, { fontWeight: 'bold', color: theme.secondary }]}>
                                                        Expert Response: "{bm.expert_response}"
                                                    </Text>
                                                </View>
                                                {bm.expert_source && (
                                                    <Text style={{ fontSize: 8, color: theme.textLight, marginTop: 6, fontStyle: 'italic', textAlign: 'right' }}>
                                                        Source: {bm.expert_source.replace(/Playbook:\s*/i, "")}
                                                    </Text>
                                                )}
                                            </View>
                                        );
                                    })}
                                </View>
                            )}

                            {/* --- ACTION PLAN & EVIDENCE --- */}
                            {showDetailedContent && (supervisorReport?.action_plan || supervisorReport?.recommended_reading) && (
                                <View break>
                                    <View style={styles.sectionHeader}>
                                        <Text style={styles.sectionTitle}>Action Plan & Evidence</Text>
                                    </View>

                                    {/* Action Plan Grid */}
                                    {supervisorReport?.action_plan && (
                                        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 15 }}>
                                            {/* Immediate Actions */}
                                            {supervisorReport.action_plan.immediate_actions && supervisorReport.action_plan.immediate_actions.length > 0 && (
                                                <View style={{ flex: 1, padding: 10, backgroundColor: '#ECFDF5', borderRadius: 6, borderLeftWidth: 3, borderLeftColor: theme.success }}>
                                                    <Text style={[styles.cardTitle, { color: theme.success }]}>Immediate Actions</Text>
                                                    {supervisorReport.action_plan.immediate_actions.map((action, i) => (
                                                        <Text key={i} style={[styles.textBody, { marginBottom: 4 }]}>• {action}</Text>
                                                    ))}
                                                </View>
                                            )}

                                            {/* Long Term Goals */}
                                            {supervisorReport.action_plan.long_term_goals && supervisorReport.action_plan.long_term_goals.length > 0 && (
                                                <View style={{ flex: 1, padding: 10, backgroundColor: '#EFF6FF', borderRadius: 6, borderLeftWidth: 3, borderLeftColor: theme.accent }}>
                                                    <Text style={[styles.cardTitle, { color: theme.accent }]}>Long Term Goals</Text>
                                                    {supervisorReport.action_plan.long_term_goals.map((goal, i) => (
                                                        <Text key={i} style={[styles.textBody, { marginBottom: 4 }]}>• {goal}</Text>
                                                    ))}
                                                </View>
                                            )}
                                        </View>
                                    )}

                                    {/* Supervision Focus */}
                                    {supervisorReport?.action_plan?.supervision_focus && (
                                        <View style={[styles.card, { borderLeftWidth: 4, borderLeftColor: theme.secondary, marginBottom: 15 }]}>
                                            <Text style={styles.cardTitle}>Supervision Focus</Text>
                                            <Text style={styles.textBody}>{supervisorReport.action_plan.supervision_focus}</Text>
                                        </View>
                                    )}

                                    {/* --- NEXT STEPS FOR PATIENT --- */}
                                    {/* {consultationData?.nextplan && (
                                <View style={{ marginTop: 15 }} break={false}>
                                    <View style={styles.sectionHeader}>
                                        <Text style={styles.sectionTitle}>Preparation For Next Session</Text>
                                    </View>
                                    <View style={[styles.card, { backgroundColor: '#F0FDFA', borderLeftWidth: 4, borderLeftColor: '#0D9488' }]}>
                                        <Text style={[styles.cardTitle, { color: '#0F766E' }]}>Future Plan</Text>
                                        <Text style={styles.textBody}>
                                            {consultationData.nextplan}
                                        </Text>
                                    </View>
                                </View>
                            )} */}

                                    {/* --- SCORE JUSTIFICATION --- */}
                                    {(() => {
                                        if (!supervisorReport?.score_justifications) return null;

                                        // Filter out optional competencies
                                        const validJustifications = Object.entries(supervisorReport.score_justifications).filter(([key, val]) => {
                                            const skill = supervisorReport.counsellor_interventions_and_skills?.[key];
                                            // If skill is marked optional, do not show in justification (it wasn't required)
                                            if (skill?.optional) return false;
                                            return true;
                                        });

                                        if (validJustifications.length === 0) return null;

                                        return (
                                            <View wrap={false}>
                                                <View style={[styles.sectionHeader, { marginTop: 15, marginBottom: 10, flexDirection: 'row', alignItems: 'center' }]}>
                                                    <Text style={styles.sectionTitle}>⚠️ Score Justification & Missed Opportunities</Text>
                                                </View>
                                                <Text style={{ fontSize: 9, color: theme.textLight, marginBottom: 12, lineHeight: 1.4 }}>
                                                    The following competencies received a score of <Text style={{ fontWeight: 'bold' }}>0/{scaleMax}</Text>.
                                                    This section explains the specific gaps observed and why they are clinically critical.
                                                </Text>

                                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                                                    {validJustifications.map(([key, justificationKey], i) => {
                                                        // Get static info from rubric JSON (handle missing keys safely)
                                                        const rubricInfo = scoreRubric[key] || {};
                                                        const title = key.replace(/_/g, " ").toUpperCase();

                                                        // Resolve the full explanation text using the signal key (justificationKey)
                                                        let justificationText = justificationKey;
                                                        if (rubricInfo.near_miss_patterns) {
                                                            const pattern = rubricInfo.near_miss_patterns.find(p => p.observed && p.observed.includes(justificationKey));
                                                            if (pattern) {
                                                                justificationText = pattern.explanation;
                                                            }
                                                        }

                                                        return (
                                                            <View key={i} style={[styles.card, { width: '48%', marginBottom: 10, padding: 0, overflow: 'hidden', borderLeftWidth: 4, borderLeftColor: theme.secondary }]}>

                                                                {/* Header */}
                                                                <View style={{ backgroundColor: '#FFF5F5', padding: 8, borderBottomWidth: 1, borderBottomColor: '#FFE0E0' }}>
                                                                    <Text style={{ fontSize: 9, fontWeight: 'bold', color: theme.secondary, letterSpacing: 0.5 }}>
                                                                        {title}
                                                                    </Text>
                                                                </View>

                                                                <View style={{ padding: 8 }}>
                                                                    {/* Definition / Description */}
                                                                    {rubricInfo.description && (
                                                                        <Text style={{ fontSize: 9, color: theme.textLight, marginBottom: 6, fontStyle: 'italic' }}>
                                                                            {rubricInfo.description}
                                                                        </Text>
                                                                    )}

                                                                    {/* Justification Text */}
                                                                    <Text style={{ fontSize: 9, color: '#374151', marginBottom: 8, lineHeight: 1.3 }}>
                                                                        <Text style={{ fontWeight: 'bold' }}>Analysis: </Text>
                                                                        {justificationText}
                                                                    </Text>

                                                                    {/* Divider */}
                                                                    <View style={{ height: 1, backgroundColor: '#E5E7EB', marginBottom: 6 }} />

                                                                    {/* Clinical Importance */}
                                                                    {rubricInfo.why_required && (
                                                                        <View>
                                                                            <Text style={{ fontSize: 8, color: '#6B7280', fontStyle: 'italic' }}>
                                                                                <Text style={{ fontWeight: 'bold', color: '#4B5563' }}>Impact: </Text>
                                                                                {rubricInfo.why_required}
                                                                            </Text>
                                                                        </View>
                                                                    )}
                                                                </View>
                                                            </View>
                                                        );
                                                    })}
                                                </View>
                                            </View>
                                        );
                                    })()}

                                    {/* Mandatory Competency Guides (Aggregated) */}
                                    {(() => {
                                        const aggregatedResources = [];
                                        if (supervisorReport?.counsellor_interventions_and_skills) {
                                            Object.values(supervisorReport.counsellor_interventions_and_skills).forEach(skill => {
                                                if (skill.optional === false && skill.recommended_resources) {
                                                    if (Array.isArray(skill.recommended_resources)) {
                                                        aggregatedResources.push(...skill.recommended_resources);
                                                    }
                                                }
                                            });
                                        }

                                        if (aggregatedResources.length === 0) return null;

                                        // Deduplicate by title
                                        let uniqueResources = aggregatedResources.filter((v, i, a) => a.findIndex(t => (t.title === v.title)) === i);

                                        // Randomly pick max 4 items
                                        if (uniqueResources.length > 4) {
                                            uniqueResources = uniqueResources.sort(() => 0.5 - Math.random()).slice(0, 4);
                                        }

                                        return (
                                            <View>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, marginTop: 10 }}>
                                                    <Text style={[styles.sectionTitle, { fontSize: 12, marginBottom: 0 }]}>Recommended Online Material (Mandatory Focus)</Text>
                                                </View>
                                                <View style={[styles.card, { padding: 12 }]}>
                                                    <View style={{ gap: 8 }}>
                                                        {uniqueResources.map((res, idx) => (
                                                            <View key={idx} style={{ flexDirection: 'column' }}>
                                                                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                    <Text style={{ fontSize: 10, color: '#374151', fontWeight: 'bold' }}>• {res.title}</Text>
                                                                    {res.quality && (
                                                                        <Text style={{ fontSize: 8, color: '#6B7280', marginLeft: 4, fontStyle: 'italic' }}>({res.quality})</Text>
                                                                    )}
                                                                </View>
                                                                {res.link && (
                                                                    <Text style={{ fontSize: 9, color: '#2563EB', textDecoration: 'none', marginLeft: 10 }}>{res.link}</Text>
                                                                )}
                                                            </View>
                                                        ))}
                                                    </View>
                                                </View>
                                            </View>
                                        );
                                    })()}
                                </View>
                            )}

                            {/* --- THEMATIC FEEDBACK SUMMARY --- */}
                            {showDetailedContent && (() => {
                                // Calculate Groups
                                const groups = {};
                                conversationEntries.forEach((entry, i) => {
                                    if (entry.therapy_feedback && (entry.therapy_feedback.feedback || entry.therapy_feedback.suggestion)) {
                                        const fb = entry.therapy_feedback;
                                        // Filter for significant items
                                        if (!fb.feedback && !fb.suggestion && !fb.rubric_violation) return;

                                        let category = "General Feedback";
                                        if (fb.rubric_violation) {
                                            category = fb.rubric_violation;
                                        } else if (fb.feedback && fb.feedback.toLowerCase().includes("empathy")) {
                                            category = "Empathy & Connection";
                                        } else if (fb.feedback && fb.feedback.toLowerCase().includes("socratic")) {
                                            category = "Guided Discovery";
                                        } else if (fb.feedback && fb.feedback.toLowerCase().includes("agenda")) {
                                            category = "Structuring & Agenda";
                                        }

                                        // [FILTER] Only show Mandatory Competencies (User Request)
                                        // If the category matches a skill key, check if it's mandatory.
                                        // If it doesn't match (e.g. "General Feedback"), keep it unless strict mode is desired.
                                        if (supervisorReport?.counsellor_interventions_and_skills?.[category]) {
                                            const skillInfo = supervisorReport.counsellor_interventions_and_skills[category];
                                            if (skillInfo.feedback_type && skillInfo.feedback_type.toLowerCase() !== 'mandatory') {
                                                return; // Skip non-mandatory
                                            }
                                        }

                                        if (!groups[category]) groups[category] = [];
                                        groups[category].push({
                                            turn: i + 1,
                                            feedback: fb.feedback,
                                            suggestion: fb.suggestion,
                                            severity: fb.severity
                                        });
                                    }
                                });

                                // Only render if impactful groups exist
                                const groupKeys = Object.keys(groups);
                                if (groupKeys.length === 0) return null;



                                // Distribution: Smart Balance based on ITEM COUNT (Height)
                                // 1. Calculate weighted size of each group
                                const totalItems = groupKeys.reduce((sum, key) => sum + groups[key].length, 0);
                                const targetColSize = totalItems / 2;

                                // 2. Distribute to Left/Right to balance height
                                const leftKeys = [];
                                const rightKeys = [];
                                let currentLeftSize = 0;

                                groupKeys.forEach(key => {
                                    const groupSize = groups[key].length;
                                    // Add to Left if it's not full yet (closer to target)
                                    // Allow at least one item if left is empty
                                    if (currentLeftSize < targetColSize || leftKeys.length === 0) {
                                        leftKeys.push(key);
                                        currentLeftSize += groupSize;
                                    } else {
                                        rightKeys.push(key);
                                    }
                                });

                                return (
                                    <View break>
                                        <View style={styles.sectionHeader}>
                                            <Text style={styles.sectionTitle}>Thematic Consultation Summary</Text>
                                        </View>
                                        <Text style={{ fontSize: 9, color: theme.textLight, marginBottom: 15 }}>
                                            Aggregated transcript feedback grouped by clinical MANDATORY theme(For this session) to identify recurring patterns.
                                        </Text>

                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            {/* Left Column */}
                                            <View style={{ width: '49%' }}>
                                                {leftKeys.map((category, i) => (
                                                    <View key={i} style={[styles.card, { marginBottom: 10, borderLeftWidth: 4, borderLeftColor: theme.secondary }]}>
                                                        <View style={{ borderBottomWidth: 1, borderBottomColor: theme.border, paddingBottom: 4, marginBottom: 6 }}>
                                                            <Text style={[styles.cardTitle, { color: theme.secondary, marginBottom: 0 }]}>
                                                                {category}
                                                            </Text>
                                                        </View>
                                                        {groups[category].map((item, idx) => (
                                                            <View key={idx} style={{ marginBottom: 8 }}>
                                                                <Text style={{ fontSize: 9, color: theme.textLight, fontStyle: 'italic', lineHeight: 1.3 }}>
                                                                    <Text style={{ fontWeight: 'bold', color: theme.primary }}>TURN {item.turn}: </Text>
                                                                    "{item.suggestion}"
                                                                </Text>
                                                                {item.severity === 'critical' && <Text style={{ fontSize: 8, color: theme.danger, marginTop: 2 }}>⚠️ Critical Issue</Text>}
                                                            </View>
                                                        ))}
                                                    </View>
                                                ))}
                                            </View>

                                            {/* Right Column */}
                                            <View style={{ width: '49%' }}>
                                                {rightKeys.map((category, i) => (
                                                    <View key={i} style={[styles.card, { marginBottom: 10, borderLeftWidth: 4, borderLeftColor: theme.secondary }]}>
                                                        <View style={{ borderBottomWidth: 1, borderBottomColor: theme.border, paddingBottom: 4, marginBottom: 6 }}>
                                                            <Text style={[styles.cardTitle, { color: theme.secondary, marginBottom: 0 }]}>
                                                                {category}
                                                            </Text>
                                                        </View>
                                                        {groups[category].map((item, idx) => (
                                                            <View key={idx} style={{ marginBottom: 8 }}>
                                                                <Text style={{ fontSize: 9, color: theme.textLight, fontStyle: 'italic', lineHeight: 1.3 }}>
                                                                    <Text style={{ fontWeight: 'bold', color: theme.primary }}>TURN {item.turn}: </Text>
                                                                    "{item.suggestion}"
                                                                </Text>
                                                                {item.severity === 'critical' && <Text style={{ fontSize: 8, color: theme.danger, marginTop: 2 }}>⚠️ Critical Issue</Text>}
                                                            </View>
                                                        ))}
                                                    </View>
                                                ))}
                                            </View>
                                        </View>
                                    </View>
                                );

                            })()}


                            {/* --- TRANSCRIPT OR REMOTE FEEDBACK --- */}
                            {showTranscript && (
                                <View break>
                                    <View style={styles.sectionHeader}>
                                        <Text style={styles.sectionTitle}>
                                            {consultationData.transcript_id ? "Turn-by-Turn Feedback" : "Session Transcript & Annotations"}
                                        </Text>
                                    </View>

                                    {/* REMOTE MODE: Only hide text if we couldn't fetch it (empty doctor/patient) or it is masked */}
                                    {consultationData.transcript_id && (!conversationEntries[0]?.doctor || !conversationEntries[0]?.patient || conversationEntries[0]?.doctor === "[Secure Remote Content]") ? (
                                        <View>
                                            <View style={[styles.card, { backgroundColor: '#F3F4F6', flexDirection: 'row', alignItems: 'center', marginBottom: 15 }]}>
                                                <Text style={{ fontSize: 14, marginRight: 8 }}>🔒</Text>
                                                <Text style={[styles.textBody, { color: theme.textLight }]}>
                                                    Session transcript is stored securely on the remote server.
                                                    Below is the turn-by-turn clinical feedback derived from the analysis.
                                                </Text>
                                            </View>

                                            {/* Feedback Table Header */}
                                            <View style={{ flexDirection: 'row', backgroundColor: theme.bgLight, padding: 8, borderBottomWidth: 1, borderBottomColor: theme.border }}>
                                                <Text style={{ fontSize: 9, fontWeight: 'bold', width: '10%' }}>Turn</Text>
                                                <Text style={{ fontSize: 9, fontWeight: 'bold', width: '20%' }}>Status</Text>
                                                <Text style={{ fontSize: 9, fontWeight: 'bold', width: '70%' }}>Suggestion / Feedback</Text>
                                            </View>

                                            {/* Feedback Rows */}
                                            {conversationEntries.map((entry, i) => {
                                                // Only show rows that have feedback
                                                if (!entry.therapy_feedback || (!entry.therapy_feedback.feedback && !entry.therapy_feedback.suggestion && !entry.therapy_feedback.rubric_violation)) return null;

                                                const fb = entry.therapy_feedback;
                                                const isCritical = fb.severity === 'critical' || fb.severity === 'significant';

                                                return (
                                                    <View key={i} style={{ flexDirection: 'row', padding: 8, borderBottomWidth: 1, borderBottomColor: theme.border, backgroundColor: isCritical ? '#FEF2F2' : '#FFFFFF' }}>
                                                        <Text style={{ fontSize: 9, width: '10%', color: theme.textLight }}>#{i + 1}</Text>
                                                        <Text style={{ fontSize: 9, width: '20%', color: isCritical ? theme.danger : theme.secondary, fontWeight: isCritical ? 'bold' : 'normal' }}>
                                                            {isCritical ? "Needs Review" : (fb.needs_improvement ? "Optimization" : "Note")}
                                                        </Text>
                                                        <View style={{ width: '70%' }}>
                                                            {fb.rubric_violation && (
                                                                <Text style={{ fontSize: 9, color: theme.danger, fontWeight: 'bold', marginBottom: 2 }}>
                                                                    {fb.rubric_violation}
                                                                </Text>
                                                            )}
                                                            {fb.feedback && (
                                                                <Text style={{ fontSize: 9, color: theme.text, marginBottom: 2 }}>
                                                                    {fb.feedback}
                                                                </Text>
                                                            )}
                                                            {fb.suggestion && (
                                                                <Text style={{ fontSize: 9, color: theme.text, fontStyle: 'italic' }}>
                                                                    Suggestion: "{fb.suggestion}"
                                                                </Text>
                                                            )}
                                                        </View>
                                                    </View>
                                                );
                                            })}
                                        </View>
                                    ) : (
                                        // LOCAL MODE: Full Transcript (Existing Logic)
                                        <View>
                                            {conversationEntries.map((entry, i) => {
                                                // Check if this turn is already covered in Expert Alignment
                                                const isExpertMatch = topBenchmarkTurnIndices.has(i + 1);

                                                return (
                                                    <View key={i} wrap={false} style={styles.transcriptItem}>
                                                        <Text style={{ fontSize: 8, color: theme.textLight, textAlign: 'center', marginBottom: 5 }}>- TURN {i + 1} -</Text>

                                                        {entry.patient && (
                                                            <View style={styles.dialogueRow}>
                                                                <Text style={[styles.speakerLabel, { color: theme.secondary }]}>PATIENT</Text>
                                                                <View style={[styles.bubble, styles.patBubble]}>
                                                                    {entry.emotion && Object.keys(entry.emotion).length > 0 && (
                                                                        <Text style={{ fontSize: 8, fontWeight: 'bold', color: theme.textLight, marginBottom: 2, textTransform: 'uppercase' }}>
                                                                            [{Object.entries(entry.emotion).reduce((a, b) => a[1] > b[1] ? a : b)[0]}]
                                                                        </Text>
                                                                    )}
                                                                    <Text style={styles.textBody}>{entry.patient}</Text>
                                                                </View>
                                                            </View>
                                                        )}
                                                        {entry.doctor && (
                                                            <View style={[styles.dialogueRow, { marginBottom: 5 }]}>
                                                                <Text style={[styles.speakerLabel, { color: theme.textLight }]}>DOCTOR</Text>
                                                                <View style={[styles.bubble, styles.docBubble]}>
                                                                    <Text style={styles.textBody}>{entry.doctor}</Text>
                                                                </View>
                                                            </View>
                                                        )}

                                                        {/* Feedback / Suggestion Injection */}
                                                        {entry.therapy_feedback && (entry.therapy_feedback.feedback || entry.therapy_feedback.suggestion) && !isExpertMatch && (
                                                            <View style={{ marginTop: 8, padding: 8, backgroundColor: '#FEF2F2', borderRadius: 4, borderLeftWidth: 3, borderLeftColor: theme.danger }}>
                                                                {entry.therapy_feedback.feedback && (
                                                                    <Text style={{ fontSize: 9, color: theme.danger, fontWeight: 'bold', marginBottom: 2 }}>
                                                                        FEEDBACK: {entry.therapy_feedback.feedback}
                                                                    </Text>
                                                                )}
                                                                {entry.therapy_feedback.suggestion && (
                                                                    <Text style={{ fontSize: 9, color: theme.text, fontStyle: 'italic' }}>
                                                                        Suggestion: "{entry.therapy_feedback.suggestion}"
                                                                    </Text>
                                                                )}
                                                            </View>
                                                        )}
                                                    </View>
                                                );
                                            })}
                                        </View>
                                    )}
                                </View>
                            )}
                            {/* --- APPENDIX: FORMULATION TOOLS --- */}
                            {showDetailedContent && supervisorReport?.appendix_tools && supervisorReport.appendix_tools.length > 0 && (
                                <View break>
                                    <View style={[styles.sectionHeader, { flexDirection: 'row', alignItems: 'center', marginBottom: 15 }]}>
                                        <Text style={styles.sectionTitle}>Appendix: Formulation Tools</Text>
                                    </View>

                                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                                        {supervisorReport.appendix_tools.map((tool, i) => (
                                            <View key={i} style={[styles.card, { width: '48%', marginBottom: 10, padding: 12 }]}>
                                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                                    <Text style={{ fontSize: 9, fontWeight: 'bold', color: theme.primary, width: '85%' }}>
                                                        {tool.title}
                                                    </Text>
                                                    {tool.quality && (
                                                        <View style={{ backgroundColor: '#F3F4F6', paddingHorizontal: 4, paddingVertical: 2, borderRadius: 4 }}>
                                                            <Text style={{ fontSize: 7, color: '#6B7280', textTransform: 'uppercase' }}>{tool.quality}</Text>
                                                        </View>
                                                    )}
                                                </View>

                                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                                                    <Text style={{ fontSize: 8, color: theme.textLight }}>
                                                        For: <Text style={{ fontWeight: 'bold' }}>{tool.competency ? tool.competency.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) : 'General'}</Text>
                                                    </Text>
                                                </View>

                                                {tool.link && (
                                                    <Text style={{ fontSize: 8, color: '#2563EB', textDecoration: 'none' }}>
                                                        {tool.link}
                                                    </Text>
                                                )}
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            )}

                            {/* --- THERAPIST SELF-REFLECTION (Value-added supervision section) --- */}
                            {!isGuideOnly && therapistReflection && (
                                <View style={{ marginTop: 20, paddingTop: 15, borderTopWidth: 2, borderTopColor: '#F3F4F6', marginBottom: 20 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                                        <View style={{ width: 4, height: 14, backgroundColor: theme.primary, marginRight: 8, borderRadius: 2 }} />
                                        <Text style={[styles.sectionTitle, { marginBottom: 0 }]}>Clinician Self-Reflection</Text>
                                    </View>

                                    <View style={{ gap: 12 }}>
                                        {therapistReflection.effective && (
                                            <View style={{ backgroundColor: '#F0FDF4', padding: 8, borderRadius: 6, borderLeftWidth: 3, borderLeftColor: theme.success }}>
                                                <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#166534', marginBottom: 2 }}>What felt most effective?</Text>
                                                <Text style={{ fontSize: 9, color: '#14532D', lineHeight: 1.4 }}>{therapistReflection.effective}</Text>
                                            </View>
                                        )}

                                        {therapistReflection.stuck && (
                                            <View style={{ backgroundColor: '#FFF7ED', padding: 8, borderRadius: 6, borderLeftWidth: 3, borderLeftColor: '#F97316' }}>
                                                <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#9A3412', marginBottom: 2 }}>Where did you feel stuck OR deviated from the plan?</Text>
                                                <Text style={{ fontSize: 9, color: '#7C2D12', lineHeight: 1.4 }}>{therapistReflection.stuck}</Text>
                                            </View>
                                        )}

                                        {therapistReflection.next_time && (
                                            <View style={{ backgroundColor: '#EFF6FF', padding: 8, borderRadius: 6, borderLeftWidth: 3, borderLeftColor: theme.primary }}>
                                                <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#1E40AF', marginBottom: 2 }}>What would you try differently next session?</Text>
                                                <Text style={{ fontSize: 9, color: '#1E3A8A', lineHeight: 1.4 }}>{therapistReflection.next_time}</Text>
                                            </View>
                                        )}
                                    </View>
                                </View>
                            )}

                            {!isGuideOnly && (
                                <View style={{ marginTop: 15, padding: 10, backgroundColor: '#F9FAFB', borderRadius: 8, borderStyle: 'dashed', borderWidth: 1, borderColor: '#D1D5DB' }}>
                                    <Text style={{ fontSize: 8, color: '#6B7280', textAlign: 'center', fontStyle: 'italic' }}>
                                        End of Consultation Review. For guidance on interpreting these scores, please refer to the "Clinical Competency Evaluation Guide".
                                    </Text>
                                </View>
                            )}
                        </>
                    )}

                    {/* --- EXPLANATION SECTION (Standalone Guide Only) --- */}
                    {isGuideOnly && (
                        <>
                            <View style={styles.explanationSection}>
                                <View style={{ marginBottom: 10 }}>
                                    <Text style={styles.sectionTitle}>Clinical Competency Evaluation, Explanation & Interpretation Guide</Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        Purpose of the Scoring System
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        This scoring system is designed to support clinical supervision, training, and quality assurance, not examination or punitive assessment. Its goal is to evaluate observable therapeutic competencies demonstrated within a specific therapy session, while accounting for clinical context, session phase, and case complexity. Scores reflect what was demonstrated in the session, not the clinician’s overall capability or professional standing.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        What Is Being Evaluated
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        Each therapy session is evaluated across a defined set of competencies, aligned with evidence-based psychotherapy frameworks (e.g., CBT, REBT, SFBT).
                                        Examples include:


                                        Each competency is evaluated independently, based solely on observable behaviors and dialogue in the session transcript.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, marginTop: 4, fontStyle: 'italic' }}>
                                        - Agenda setting and session structure
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>
                                        - Therapeutic collaboration
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>
                                        - Emotional processing
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>
                                        - Cognitive or behavioral interventions
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>
                                        - Application of change methods
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        How Scores Are Generated
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        1. Pointer Extraction (Observation Layer):
                                        The system first extracts explicit, observable pointers from the session (e.g., whether homework was assigned, whether feedback was bidirectional).
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, textDecoration: 'underline' }}>
                                        Key principles:
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>- Pointers are extracted conservatively</Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>- No inference or assumption is made</Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>- If evidence is unclear or implicit, the signal is marked absent</Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>This ensures consistency and auditability.</Text>

                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        2. Competency Scoring:
                                        Each competency has a predefined scoring ladder (0–{scaleMax} scale) that maps combinations of pointers to score levels.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, textDecoration: 'underline' }}>
                                        Important characteristics
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>- Scoring rules are defined as per the industry Standard</Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>- Advanced skills do not compensate for missing foundational steps</Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>- Optional excellence can increase scores but cannot create depth on its own</Text>

                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        3. Domain Aggregation and Overall Competency:

                                        Domain scores are computed as averages of demonstrated competencies. It is weighted based on clinical importance. Phase-specific caps and safety rules are applied where it is appropriate
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        How to Interpret Scores (Critical)
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        A score of 0 indicates that the competency was not demonstrated during the evaluated session or was not appropriate given the clinical phase or session focus.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, fontStyle: 'italic' }}>
                                        Zero scores do not imply clinician deficiency and should be interpreted in relation to session objectives and clinical context.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, textDecoration: 'underline' }}>
                                        What Higher Scores Mean
                                    </Text>
                                    {scaleMax === 4 ? (
                                        <>
                                            <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>1: Needs Improvement</Text>
                                            <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>2: Satisfactory</Text>
                                            <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>3–4: Good / Very Good</Text>
                                        </>
                                    ) : (
                                        <>
                                            <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>2–3: Basic or emerging demonstration</Text>
                                            <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>4–5: Competent, clinically appropriate use</Text>
                                            <Text style={{ fontSize: 9, color: theme.textLight, marginLeft: 10, fontStyle: 'italic' }}>6: Strong, consistent, and well-integrated use</Text>
                                        </>
                                    )}
                                    <Text style={{ fontSize: 9, color: theme.textLight, fontStyle: 'italic' }}>
                                        Scores should always be interpreted in context, not in isolation.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        Phase Awareness
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        Sessions are evaluated relative to their clinical phase, such as:
                                        Crisis
                                        Stabilization
                                        Change

                                        Certain competencies (e.g., homework, cognitive restructuring) may be optional or inappropriate in early phases. The system accounts for this when interpreting scores and generating feedback.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        Feedback and Explanations
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        Numeric scores are accompanied by:
                                        Clinical explanations of what each competency requires
                                        Justifications when a score is low or zero
                                        Near-miss coaching that explains what was close to meeting criteria

                                        This mirrors how human supervisors provide feedback in training settings.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        What the System Does Not Do
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        It does not diagnose clinicians
                                        It does not infer intent or internal skill
                                        It does not penalize appropriate clinical prioritization
                                        It does not replace human supervision

                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 8, fontWeight: 'bold' }}>
                                        Intended Use
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        This scoring methodology is intended for:
                                        Training and supervision
                                        Skill development and reflection
                                        Institutional quality review
                                        Structured feedback in educational settings
                                    </Text>
                                    <Text style={{ fontSize: 10, color: theme.success, marginTop: 12, fontWeight: 'bold' }}>
                                        Key Principle to Remember (VERY IMPORTANT)
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        Scores reflect session-level demonstration, not clinician-level ability.
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 6, fontStyle: 'italic' }}>
                                        In some sessions, planned structure is appropriately adapted in response to emerging emotional material. When this occurs, optimization suggestions should be read as guidance for future sessions, not as errors in clinical judgment.
                                    </Text>
                                </View>
                                <Text style={[styles.footerText, { fontWeight: 'bold' }]}>Understanding this Report based on Example:</Text>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                    <View style={{ width: '48%' }}>
                                        <Text style={styles.footerText}><Text style={{ fontWeight: 'bold' }}>Common Pitfall:</Text> "Focus on the good things."</Text>
                                        <Text style={styles.footerText}><Text style={{ fontWeight: 'bold' }}>Clinical Rationale:</Text> "Depression often includes cognitive discounting."</Text>
                                        <Text style={[styles.footerText, { fontStyle: 'italic' }]}>(Meaning: Trying to force positivity feels invalidating.)</Text>
                                    </View>
                                    <View style={{ width: '48%' }}>
                                        <Text style={styles.footerText}><Text style={{ fontWeight: 'bold' }}>Pitfall:</Text> The Clinician's mistake (using suboptimal intervention).</Text>
                                        <Text style={styles.footerText}><Text style={{ fontWeight: 'bold' }}>Rationale:</Text> Why that mistake is counter-therapeutic.</Text>
                                        <Text style={styles.footerText}><Text style={{ fontWeight: 'bold' }}>Summary:</Text> Flagging toxic positivity vs proper validation.</Text>
                                    </View>
                                </View>

                                {/* --- NEW SECTION EXPLANATIONS (MTO, AIS, DEPTH) --- */}
                                <View style={{ marginTop: 15 }}>
                                    {/* 1. MTO */}
                                    <Text style={{ fontSize: 9, color: theme.primary, marginTop: 8, fontWeight: 'bold' }}>
                                        ✅ 1. "Missed Therapeutic Opportunities" (MTO)
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontWeight: 'bold', textDecoration: 'underline' }}>
                                        What this means clinically
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 2 }}>
                                        This is NOT "you did something wrong." It is: "Here is a point in the session where you could have gone deeper or taken a different route."
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        <Text style={{ fontWeight: 'bold' }}>Examples:</Text>
                                        {"\n"}• "When the patient mentioned feeling like a failure as a father, you could have explored the meaning and origin of this belief."
                                        {"\n"}• "When the patient expressed insomnia, a brief psychoeducation on the arousal system could have been helpful."
                                    </Text>

                                    {/* 2. AIS */}
                                    <Text style={{ fontSize: 9, color: theme.primary, marginTop: 12, fontWeight: 'bold' }}>
                                        ✅ 2. "Alternative Interventions / Strategies" (AIS)
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontWeight: 'bold', textDecoration: 'underline' }}>
                                        What this means clinically
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 2 }}>
                                        Supervisors always give: ✔ what you did vs ✔ what else you could have done
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        <Text style={{ fontWeight: 'bold' }}>Examples:</Text>
                                        {"\n"}• "Instead of reassurance, a downward-arrow could have deepened the core belief."
                                        {"\n"}• "A brief behavioral activation plan could support stabilization."
                                    </Text>

                                    {/* 3. DEPTH */}
                                    <Text style={{ fontSize: 9, color: theme.primary, marginTop: 12, fontWeight: 'bold' }}>
                                        ✅ 3. "Points Where Depth or Clarity Could Be Enhanced" (DEPTH)
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontWeight: 'bold', textDecoration: 'underline' }}>
                                        This is different from "Missed Opportunities."
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 2 }}>
                                        <Text style={{ fontWeight: 'bold' }}>"Missed opportunities"</Text> = what you could have added
                                        {"\n"}
                                        <Text style={{ fontWeight: 'bold' }}>"Depth/clarity"</Text> = existing intervention could be tightened or structured better
                                    </Text>
                                    <Text style={{ fontSize: 9, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                                        <Text style={{ fontWeight: 'bold' }}>Examples:</Text>
                                        {"\n"}• "The query about meaning could be sharpened by anchoring it to a concrete event."
                                        {"\n"}• "You named the emotion but did not check intensity or meaning."
                                    </Text>
                                </View>
                                {/* --- SCORE DESCRIPTIONS --- */}
                                <View>
                                    {supervisorReport?.scoring_breakdown && (
                                        <View style={{ marginTop: 20, marginBottom: 15, padding: 12, backgroundColor: '#F9FAFB', borderRadius: 4 }}>
                                            <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: theme.primary, marginBottom: 8 }}>
                                                Score Descriptions
                                            </Text>
                                            <View style={{ flexDirection: 'row', gap: 10 }}>
                                                <View style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: theme.danger, marginBottom: 3 }}>
                                                        Clinical Competency
                                                    </Text>
                                                    <Text style={{ fontSize: 8, color: theme.textLight, lineHeight: 1.4 }}>
                                                        "Was this skill demonstrated adequately for this session, phase, and case?" Measures the clinician's overall competency across all therapy domains (structure, cognitive, behavioral, relationship, emotional). Calculated using domain-based weighted aggregation with phase caps and floor requirements.
                                                    </Text>
                                                </View>
                                                <View style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: theme.success, marginBottom: 3 }}>
                                                        Safety Compliance
                                                    </Text>
                                                    <Text style={{ fontSize: 8, color: theme.textLight, lineHeight: 1.4 }}>
                                                        Evaluates adherence to critical safety protocols including suicide risk assessment, harm prevention, and ethical guidelines. Scores below 80% indicate safety concerns requiring immediate attention.
                                                    </Text>
                                                </View>
                                                <View style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: theme.accent, marginBottom: 3 }}>
                                                        Clinical Optimization
                                                    </Text>
                                                    <Text style={{ fontSize: 8, color: theme.textLight, lineHeight: 1.4 }}>
                                                        "How could an already-competent clinician refine or deepen this skill over time?". Assesses advanced clinical skills and optimization opportunities beyond basic competency. Includes nuanced interventions, expert-level techniques, and refinement areas for professional development.
                                                    </Text>
                                                </View>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>

                            {/* --- PHASE-BASED SKILL REQUIREMENTS MATRIX --- */}
                            {/* --- PHASE-BASED SKILL REQUIREMENTS MATRIX (CBT Only) --- */}
                            {/* --- PHASE-BASED SKILL REQUIREMENTS MATRIX (CBT & NIMHANS) --- */}
                            <View break>
                                <View style={styles.sectionHeader}>
                                    <Text style={styles.sectionTitle}>
                                        {getEvaluationFramework().includes("NIMHANS")
                                            ? "PHASE-BASED SKILL REQUIREMENTS (Authoritative Matrix)"
                                            : "PHASE-BASED SKILL REQUIREMENTS (Authoritative Matrix - CBT)"}
                                    </Text>
                                </View>
                                <Text style={{ fontSize: 9, color: theme.textLight, marginBottom: 10 }}>
                                    This matrix outlines mandatory and optional requirements across different therapeutic phases, based on institutional standard guidelines:
                                </Text>

                                {/* LEGEND */}
                                <View style={{ marginBottom: 15 }}>
                                    <Text style={{ fontSize: 9, fontWeight: 'bold', marginBottom: 4 }}>LEGEND</Text>
                                    <View style={{ flexDirection: 'row', gap: 20 }}>
                                        <Text style={{ fontSize: 9 }}>Y - Required / Mandatory</Text>
                                        <Text style={{ fontSize: 9 }}>O - Optional / Context-Dependent</Text>
                                        <Text style={{ fontSize: 9 }}>X - Not expected in this phase</Text>
                                    </View>
                                </View>

                                {/* TABLE HEADER */}
                                <View style={{ flexDirection: 'row', borderBottomWidth: 1.5, borderBottomColor: '#000', paddingBottom: 5, marginBottom: 5, backgroundColor: '#F9FAFB' }}>
                                    <Text style={{ fontSize: 9, fontWeight: 'bold', width: '40%', paddingLeft: 4 }}>Competency Area</Text>
                                    <Text style={{ fontSize: 9, fontWeight: 'bold', width: '20%', textAlign: 'center' }}>Crisis</Text>
                                    <Text style={{ fontSize: 9, fontWeight: 'bold', width: '20%', textAlign: 'center' }}>Stabilization</Text>
                                    <Text style={{ fontSize: 9, fontWeight: 'bold', width: '20%', textAlign: 'center' }}>Change</Text>
                                </View>

                                {/* TABLE BODY */}
                                {(getEvaluationFramework().includes("NIMHANS")
                                    ? [
                                        { name: "Session Structuring & Rapport", crisis: "Y", stab: "Y", change: "Y" },
                                        { name: "Assessment & Exploration Skills", crisis: "Y", stab: "Y", change: "Y" },
                                        { name: "Therapeutic Presence & Stance", crisis: "Y", stab: "Y", change: "O" },
                                        { name: "Case Formulation & Focus", crisis: "O", stab: "Y", change: "Y" },
                                        { name: "Intervention Relevance & Effectiveness", crisis: "O", stab: "O", change: "Y" },
                                        { name: "Goal Setting & Action Planning", crisis: "O", stab: "O", change: "Y" },
                                        { name: "Time Management", crisis: "O", stab: "Y", change: "Y" },
                                        { name: "Termination & Continuity Planning", crisis: "O", stab: "O", change: "Y" },
                                        { name: "Ethical Stance & Professionalism", crisis: "Y", stab: "O", change: "O" },
                                        { name: "Risk Screening & Safety Assessment", crisis: "Y", stab: "Y", change: "O" }
                                    ]
                                    : [
                                        { name: "Agenda Setting", crisis: "O", stab: "Y", change: "Y" },
                                        { name: "Feedback", crisis: "O", stab: "Y", change: "Y" },
                                        { name: "Collaboration", crisis: "Y", stab: "Y", change: "Y" },
                                        { name: "Pacing", crisis: "Y", stab: "Y", change: "O" },
                                        { name: "Interpersonal Effectiveness", crisis: "Y", stab: "Y", change: "O" },
                                        { name: "Eliciting Emotion", crisis: "Y", stab: "Y", change: "O" },
                                        { name: "Eliciting Cognitions", crisis: "O", stab: "O", change: "Y" },
                                        { name: "Guided Discovery", crisis: "O", stab: "O", change: "Y" },
                                        { name: "Conceptual Integration", crisis: "O", stab: "Y", change: "Y" },
                                        { name: "Behavioral Interventions", crisis: "O", stab: "O", change: "Y" },
                                        { name: "Change Methods", crisis: "O", stab: "O", change: "Y" },
                                        { name: "Homework", crisis: "O", stab: "Y", change: "Y" }
                                    ]
                                ).map((row, i) => (
                                    <View key={i} style={{ flexDirection: 'row', paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: '#E5E7EB', backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}>
                                        <Text style={{ fontSize: 9, width: '40%', paddingLeft: 4 }}>{row.name}</Text>
                                        <Text style={{ fontSize: 9, width: '20%', textAlign: 'center', fontWeight: row.crisis === 'Y' ? 'bold' : 'normal' }}>{row.crisis}</Text>
                                        <Text style={{ fontSize: 9, width: '20%', textAlign: 'center', fontWeight: row.stab === 'Y' ? 'bold' : 'normal' }}>{row.stab}</Text>
                                        <Text style={{ fontSize: 9, width: '20%', textAlign: 'center', fontWeight: row.change === 'Y' ? 'bold' : 'normal' }}>{row.change}</Text>
                                    </View>
                                ))}

                                {/* FLOOR REQUIREMENTS SECTION */}
                                <View style={{ marginTop: 20, padding: 10, backgroundColor: '#F8FAFC', borderRadius: 4, borderWidth: 1, borderColor: '#CBD5E1' }}>
                                    <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 6 }}>Institutional Quality Floors (Minimum Required Scores):</Text>
                                    {getEvaluationFramework().includes("NIMHANS") ? (
                                        <View style={{ gap: 4 }}>
                                            <Text style={{ fontSize: 8 }}>• <Text style={{ fontWeight: 'bold' }}>Performance Goal:</Text> All mandatory skills should aim for {'>'}= 2.0/{scaleMax} (Competent level).</Text>
                                            <Text style={{ fontSize: 8 }}>• <Text style={{ fontWeight: 'bold' }}>Safety First:</Text> Risk screening must be assessed in all sessions presenting hopelessness.</Text>
                                        </View>
                                    ) : (
                                        <View style={{ gap: 4 }}>
                                            <Text style={{ fontSize: 8 }}>• <Text style={{ fontWeight: 'bold' }}>Expertise Floor:</Text> Core relationship and structure skills should maintain {'>'}= 3.0/{scaleMax}.</Text>
                                        </View>
                                    )}
                                </View>

                                <View style={{ marginTop: 20 }}>
                                    <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Standard References:</Text>
                                    <Text style={{ fontSize: 8, color: theme.textLight }}>
                                        {getEvaluationFramework().includes("NIMHANS")
                                            ? "Referenced NIMHANS Evaluation Framework, Institutional Clinical Guidelines (0-4 Scale)"
                                            : "CTS-R, Roth & Pilling Model, NICE Guidelines, Beck Institute, ACT Certification Standards (0-6 Scale)"}
                                    </Text>
                                </View>
                            </View>
                        </>
                    )}

                    {/* --- END pageContent wrapper --- */}
                </View>

                {/* --- PAGE FOOTER (Fixed, Copyright/PageNums/Evaluation Framework) --- */}
                <View style={styles.pageFooter} fixed>
                    <View style={{ marginBottom: 4 }}>
                        <Text style={{ fontSize: 7, color: theme.textLight, textAlign: 'center', fontStyle: 'italic' }}>
                            Evaluation Framework: {getEvaluationFramework()}
                        </Text>
                    </View>
                    <Text style={{ fontSize: 8, color: theme.textLight, textAlign: 'center' }}>© {currentYear} MindConnect by NeuralLoom Labs. All rights reserved.</Text>
                    <Text style={{ fontSize: 8, color: theme.textLight, textAlign: 'center' }} render={({ pageNumber, totalPages }) => (
                        `${pageNumber} / ${totalPages}`
                    )} />
                </View>

            </Page>
        </Document >
    );
};

export default ConsultationPDFDocument;
